import { parse, isValid, differenceInCalendarDays } from 'date-fns';
import Cookies from 'js-cookie';

import { FEATURE_ANNOUNCEMENT_LAYER_ATTRIBUTE, FEATURE_ANNOUNCEMENT_MAX_AGE_DAYS, FEATURE_ANNOUNCEMENT_SUPPORT_EMAIL, FEATURE_IDS, NEWS_CATEGORIES } from './_enums';

import { helpArticlePath } from '@/app/helpArticle/_helpers';
import { DATE_FORMATS } from '@/helpers/date';
import { isEstimateDrawingUploadEnabled } from '@/helpers/featureFlags';

// Newest first. Every item needs a unique `id`, one higher than the previous newest — it is
// what drives the unseen-updates badge in the nav rail. NEXT ID: 229
//
// `isReleased` is optional and is for an update about a feature still behind a flag: pass the
// flag, and only the users it admits see the update, its badge and its tooltip. Drop it when the
// flag goes. Read the list through `getReleasedNews`, never `news` directly, or the update leaks.
//
// `featureIds` is optional and opts the item in to a release tooltip: the app points at the
// listed elements for 30 days and then stops. Most items should not have it — see
// docs/conventions/feature-announcements.md for when it earns its place and how to pick the
// element.
//
// `helpArticleSlug` is optional and adds a "Read the Guide" link to that help centre article.
// Set it only where an article in buildores/docs covers the feature the item announces; the
// slug is the article's `slug` frontmatter, as written there.
const news = [
  {
    id: 228,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'November 3rd, 2026',
    title: 'Comparison Overview',
    description: `<p>We've added an improved overview page to comparisons. Open a comparison and it now starts on an Overview of its records: the headline totals, a card for each record, and charts of their total cost, design data and buyout dates, with a map of where each project is. The full table of parameters is still one click away on the Comparison tab.</p>`
  },
  {
    id: 227,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'October 30th, 2026',
    title: 'Hide Comparison Records',
    featureIds: [FEATURE_IDS.COMPARISON_VIEWING_TRIGGER],
    description: `<p>Take a record out of a comparison without deleting it. The new Viewing button on a comparison has a switch for each record: turn one off and its column comes off the page, and min, average, median, max and spread are worked out over the records you can still see.</p><p class='mt-3'>Exports and the Comparison block in estimate reports follow the page, so what you hand over matches what you were reading. The record an estimate's or project record's Compare tab was started from, the two records the variance compares, and the last record you are viewing cannot be hidden.</p>`
  },
  {
    id: 226,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'October 30th, 2026',
    title: 'Reorder Comparison Records',
    featureIds: [FEATURE_IDS.COMPARISON_SORT_TRIGGER],
    description: `<p>Put a comparison's records in the order you want to read them. Open the new Sort menu on a comparison and drag the records into place: the table's columns, the chart and the exports all follow.</p>`
  },
  {
    id: 225,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'October 28th, 2026',
    title: 'Compare Tab',
    featureIds: [FEATURE_IDS.ESTIMATE_COMPARE_TAB],
    description: `<p>Estimates and project records now have a Compare tab, so you can compare the one you are working on without leaving it. Click Start Comparison, and on an estimate the comparison comes prefilled with the other estimates in its project.</p><p>Pick a saved parameter layout or generate one from the estimate's own design data and cost categories, and add any other estimate or record you like. The estimate or record you started from always stays in the comparison, and the comparison also appears on the Comparisons page.</p>`
  },
  {
    id: 224,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'October 28th, 2026',
    title: 'Draw Missed Findings',
    featureIds: [FEATURE_IDS.BID_QUOTE_DRAW_TRIGGER],
    description: `<p>When AI Bid Levelling misses something on a quote, draw it in yourself. Click Draw above the quote, drag a box over the line, choose what it is, and place it on your bid package like any other finding.</p><p>Zebel reads the text and any amount from under the box. Your boxes are dashed so they stand out from Zebel's, and an editor can move, resize, change or delete them. A box you leave unplaced waits on Unmatched Findings, marked Manual.</p>`
  },
  {
    id: 223,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 26th, 2026',
    title: 'Drawings on Add Estimate',
    isReleased: isEstimateDrawingUploadEnabled,
    featureIds: [FEATURE_IDS.ESTIMATE_ADD_DRAWING_DROPZONE],
    description: `<p>Drop a drawing set PDF on the first step of Add Estimate. It uploads while you fill in the rest of the details and is added to the estimate when you continue, so the drawings are processing before you have picked a template.</p><p>The estimate summary then shows the drawing set: its first page, which opens the drawings, and its walkthroughs, or Add Walkthrough when it has none yet.</p>`
  },
  {
    id: 222,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 20th, 2026',
    title: 'System Comps',
    featureIds: [FEATURE_IDS.ESTIMATE_ADD_COMP_BUTTON],
    description: `<p>System Comps are now available to everyone. A System Comp is a smart comp that takes the best value for each cost code across all your project records, filtered how you choose, and sits alongside your Project Comps. Pick how each value is chosen: Zebel Selected, Min, Median, Max or Average.</p><p>To add one, click Add Comp and choose Add System Comp when you create an estimate or on the estimate's Comps page.</p>`
  },
  {
    id: 221,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'October 19th, 2026',
    title: 'AI Bid Levelling',
    helpArticleSlug: 'how-to-level-a-bid-quote-with-ai',
    isPinned: true,
    featureIds: [FEATURE_IDS.BID_PACKAGE_QUOTE_REVIEW_ROW],
    description: `<p>Upload a bidder's quote to their bid on a bid package, and Zebel reads it and lines it up against your package for you. Click Review in the Bidder Quote row to check each match, then save it or skip it.</p><p>You'll also see anything the quote priced that isn't in your package, and anything in your package the bidder left out.</p>`
  },
  {
    id: 220,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 16th, 2026',
    title: 'Unit Cost Rates',
    helpArticleSlug: 'how-to-use-unit-cost-rates',
    featureIds: [FEATURE_IDS.ESTIMATE_COST_SUB_FIELD_UNIT_COST_CELL],
    description: `<p>Detail rows can now be priced from unit cost rates instead of a formula. Click a detail row's unit cost and pick a rate and variant, like 5/8in drywall in Edmonton, and that rate becomes the row's unit cost until you switch back to the formula.</p><p>Rates live on the template in groups, with a dollar value for each variant, and every estimate gets its own copy when it is created, so you can update a rate on one estimate without touching the others.</p>`
  },
  {
    id: 219,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 14th, 2026',
    title: 'GCs/GRs by Week',
    featureIds: [FEATURE_IDS.GCGR_SCHEDULE_ZOOM_SLIDER],
    description: `<p>GCs/GRs can now be scheduled by the week as well as the month. Zoom the schedule in to work week by week and out to see months, and price each line at an hourly, weekly or monthly rate.</p>`
  },
  {
    id: 218,
    category: NEWS_CATEGORIES.MARKET_DATA,
    date: 'October 13th, 2026',
    title: 'Design Data in Market Data',
    helpArticleSlug: 'how-to-compare-design-data-in-market-data',
    featureIds: [FEATURE_IDS.MARKET_VALUE_TYPE_BUTTON],
    description: `<p>Switch Market Data from Cost to Design to see how design data compares with the market. Pick any market design field to chart its values across the market and your company, and see where the record you are on sits.</p><p>Below the charts, the Design Benchmarks table compares every market design field at once: the middle half of the market (P25 to P75) and its median, your company's median and, on an estimate or project record, that record's value and percentile. Click a row to chart that field. Market values need at least 5 projects from other companies, and the same filters apply to the charts and the table.</p>`
  },
  {
    id: 217,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 12th, 2026',
    title: 'GCs/GRs Allocation Updates',
    description: `<p>Allocations in GCs/GRs can now go above 100%, and changing an item's allocation in its edit dialog now applies it to every month.</p>`
  },
  {
    id: 216,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 12th, 2026',
    title: 'Bulk Set Tag UOM',
    description: `<p>You can now bulk set the UOM of cost tags from Bulk Actions on the Cost Tags page of templates and estimates.</p>`
  },
  {
    id: 215,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 10th, 2026',
    title: 'Bulk Set Tags and Detail Estimating',
    description: `<p>You can now bulk set tags and detail estimating from Bulk Actions in the detailed budget of templates and estimates.</p>`
  },
  {
    id: 214,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 7th, 2026',
    title: 'Override Total Cost',
    helpArticleSlug: 'how-to-override-the-total-cost-of-a-cost-field',
    featureIds: [FEATURE_IDS.ESTIMATE_COST_FIELD_TOTAL_COST_CELL],
    description: `<p>You can now override the total cost of a cost field with a formula in the detailed budget, the same way you override its unit cost. Click the total cost to set it, and the unit cost becomes the total divided by the quantity. Cost fields with detail estimating on still total their detail rows, and you can override the total cost of a detail row the same way.</p>`
  },
  {
    id: 213,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 2nd, 2026',
    title: 'Default Saved View',
    description: `<p>Set a saved view as the default on a template or an estimate, and its Detailed Budget and new Cost Estimate report blocks start from that view until you pick a different one.</p>`
  },
  {
    id: 212,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 1st, 2026',
    title: 'Plan Sheet Walkthroughs',
    featureIds: [FEATURE_IDS.ESTIMATE_FILES_TAB],
    description: `<p>A drawing set is easier to review when the sheets that matter are lined up in order rather than scattered across fifty pages. Upload your plan sheets in Files → Drawings, draw annotations on them, and save those annotations into walkthroughs that group related items together, so the whole team can step through the same scope one stop at a time.</p>
    <p class='mt-3'>Open a sheet to land on All Sheets, where you draw annotations, colour them, and link each one to a cost field in the detailed budget. The Filter button narrows the sheet to the colours and walkthroughs you pick. Add Walkthrough on the Walkthroughs tab creates one, and the walkthrough picker on each annotation adds it as a stop. A walkthrough plays its stops across sheets in order, framing each one on its drawing, and Back, Next Stop or the arrow keys move between them.</p>`
  },
  {
    id: 211,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'September 29th, 2026',
    title: 'Reorder Detailed Budget Columns',
    featureIds: [FEATURE_IDS.ESTIMATE_DETAILED_BUDGET_COLUMNS_TRIGGER],
    description: `<p>You can now reorder the columns in the detailed budget to any order you want, and save it as a view for future use.</p>`
  },
  {
    id: 210,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'September 29th, 2026',
    title: 'Add Cost Categories in Detailed Budget',
    description: `<p>You can now add cost categories to the detailed budget directly from the detailed budget itself.</p>`
  },
  {
    id: 209,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'September 29th, 2026',
    title: 'Unit Cost History',
    helpArticleSlug: 'how-to-use-unit-cost-history',
    featureIds: [FEATURE_IDS.UNIT_COST_HISTORY_NAV_ITEM],
    description: `<p>Remembering what you paid for a cost code last time usually means digging through two or three old estimates. Unit Cost History puts every cost code your records have priced on one page, with the most recent prices side by side and the min, median, average and max next to them.</p>
    <p class='mt-3'>The Analytics tab calls out the codes that have moved recently, the prices that look out of line, and the codes resting on only a record or two. It filters the same way the project list does, so you can narrow it to the work you actually compare against. You will find it in the nav rail on the left.</p>`
  },
  {
    id: 208,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 22nd, 2026',
    title: 'Bid Package Improvements',
    description: `<p>Zebel now tells you when the bidder you are about to prefer or select is already preferred or selected on another bid package of this estimate, and names those packages alongside their bids and your estimates. Setting a preferred bid asks you to confirm first; selecting a bid warns you in the dialog you are already in.</p>`
  },
  {
    id: 207,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 21st, 2026',
    title: 'Assign Bid Packages',
    helpArticleSlug: 'how-to-assign-a-bid-package-to-a-user',
    // Two ids because the feature has an entry point on two pages and they are never both
    // on screen: the control in the package header, and the column on the list.
    featureIds: [FEATURE_IDS.BID_PACKAGE_ASSIGN_CELL, FEATURE_IDS.BID_PACKAGE_ASSIGN_TRIGGER],
    description: `<p>Who is buying out which package is now recorded in Zebel rather than in somebody's head. Tick off everybody working a package from the control at the top of it, or straight from the new Assigned To column on the bid packages list. That column filters as well, and Assigned to Me is the first option in it.</p>`
  },
  {
    id: 206,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 18th, 2026',
    title: 'Hide Bids',
    helpArticleSlug: 'how-to-hide-a-bid-without-deleting-it',
    featureIds: [FEATURE_IDS.BID_PACKAGE_VIEWING_TRIGGER],
    description: `<p>Some bids stop being worth looking at long before you are willing to throw them away — the number that came in incomplete, the one you know you are not taking, the second submission from a bidder you already have a revised price from. Deleting it is the only way to get it off the screen, and deleting it means losing what they actually said. The Viewing button next to Sort on the bid package page is the other answer: turn a bid off and its column comes off the page, while the bid itself stays exactly where it is.</p>
    <p class='mt-3'>Each bid is a switch, with the date it came in under the bidder's name, so a package carrying two bids from the same company is still readable. The button says how many you are looking at — Viewing: 4 of 6 — so a package you tidied up weeks ago does not quietly look like a package that only ever had four bids. Turn it back on and the column comes straight back, in the position it always had.</p>
    <p class='mt-3'>Exports follow the page. Send the package to a report, to a PDF or to a CSV and only the bids you are viewing are printed, so what you hand over matches what you were reading. Your preferred or selected bid cannot be hidden, and neither can the last one you are looking at — there is always at least one bid on the page.</p>`
  },
  {
    id: 205,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 18th, 2026',
    title: 'Sort Bids',
    featureIds: [FEATURE_IDS.BID_PACKAGE_SORT_TRIGGER],
    description: `<p>Once a bid package has more than a handful of bids, the order the columns happen to be in is rarely the order you want to read them in. There is now a Sort menu next to Export on the bid package page. Order the bids lowest to highest or highest to lowest on their levelled total, newest to oldest or oldest to newest by the date the bid came in, or by any bid tag group on the package — so union shops sit together, or everything you marked scope complete comes first. Manual Order is what you start with, and it is yours to set: open it and drag the bids into whatever order you want the package to read in.</p>
    <p class='mt-3'>Under the sorts is a switch that takes one bid out of the ordering altogether. Preferred/Selected Bid Always Goes First keeps the number you are actually carrying at the left-hand edge however the rest is sorted. A bid with no tag in the group you sorted by goes to the end rather than the front, so an untagged bid never pushes its way to the top of a package.</p>
    <p class='mt-3'>Your choice is remembered per bid package, so coming back to one finds it the way you left it, and sorting one package does not change how any other one reads.</p>`
  },
  {
    id: 204,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 15th, 2026',
    title: 'Dynamic Bid Tags',
    helpArticleSlug: 'how-to-create-a-dynamic-bid-tag-group',
    description: `<p>Bid tags only stay useful while they are current, and bids do not sit still. You tag everything that came in under your estimate, two revised bids land the next morning, and you are sorting the package by labels that were true yesterday. Switch on Dynamic when you add or edit a bid tag group and that upkeep goes away: write each tag's rules once, and every bid is re-tagged whenever the package recalculates. Rules read a bid's total, how far it sits from your estimate or from the rest of the field, and whether it is selected, preferred, low, high or empty.</p>`
  },
  {
    id: 203,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'September 11th, 2026',
    title: 'Precon Team',
    featureIds: [FEATURE_IDS.PRECON_TEAM_NAV_ITEM],
    description: `<p>Knowing who is working on what during preconstruction usually lives in a spreadsheet one person keeps and everybody else guesses at. Precon Team puts it in Zebel instead: every pursuit you are chasing, who is on it, and how much of their week it is actually taking. When the next RFP lands you can see whether anyone has room for it before you say yes.</p>
    <p class='mt-3'>Summary is the quick read. It shows what the pipeline is worth, where capacity is already tight, and which opportunities need somebody to look at them. Pipeline and Team lay the same work out on a timeline, so two pursuits landing on the same estimator in the same fortnight is something you see coming instead of something you find out about on the Monday.</p>
    <p class='mt-3'>Allocation and Burn Rate are where you check the hours. You plan what a pursuit should take, and these tell you what it is taking, so the answer to "can we take this on" is a number rather than a feeling.</p>
    <p class='mt-3'>Opportunities is the list itself, with your own management statuses and deliverables on each one, so every pursuit moves through the same stages for everybody. Users is where you say who is on the team and what their time costs.</p>
    <p class='mt-3'>Precon Team is in beta. If you want it switched on for your company, or you try it and something you need is missing, reach out to support and tell us.</p>`
  },
  {
    id: 202,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 11th, 2026',
    title: 'Bid Tags',
    helpArticleSlug: 'how-to-tag-bids-in-a-bid-package',
    featureIds: [FEATURE_IDS.BID_PACKAGE_BID_TAG_CELL],
    description: `<p>Comparing bids gets hard as soon as there are more than a handful, because the thing you want to group them by is almost never the bidder's name. Bid tags let you label each bid with what you actually sort by: union or open shop, scope complete or scope light, a number you trust against one you are carrying. Set the groups up once and every bid on the package gets a row where you pick from them.</p>
    <p class='mt-3'>Tag a bid from the Bid Tags section of the bid package, or as you add it. The analytics we promised when tagging shipped are in now, so once the tags are on you can see how they are spread across the package and spot that four of your six numbers came from the same kind of shop.</p>
    <p class='mt-3'>Put the groups on a record template and every bid package you start from it already has them.</p>`
  },
  {
    id: 201,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'September 10th, 2026',
    title: 'VE / Alternates',
    helpArticleSlug: 'how-to-value-engineer-an-estimate',
    featureIds: [FEATURE_IDS.ESTIMATE_VALUE_ENGINEERING_TAB],
    description: `<p>Value engineering usually happens in a spreadsheet beside the estimate. You price a cheaper facade, write the saving down somewhere, price a cheaper one again, and by the time somebody asks what the building costs with three of the five options taken you are adding numbers by hand and hoping none of them moved. The VE / Alternates tab keeps that work in the estimate, so the answer is always current.</p>
    <p class='mt-3'>An item is one thing you are considering changing — the exterior skin, the flooring, the casework — and it covers whichever cost codes that change would come out of. Under it, each alternate is one option, priced against every one of those codes with a formula rather than a fixed number, so an option worth $2.75 a square foot stays worth $2.75 a square foot when the building grows. The None column is the estimate as it stands, and every alternate is read against it.</p>
    <p class='mt-3'>Apply an alternate and the estimate moves with it — the detailed budget, the summary and every report follow. Nothing is destroyed: switch back to None, or to a different alternate, and the others are still sitting there priced. A cost code can be covered by more than one item, so an option that touches drywall and an option that touches paint can both name the same code without fighting over it.</p>
    <p class='mt-3'>Cost codes priced in detailed estimating are value engineered on their detail rows instead of as a whole, so the saving is recorded against the line it actually comes out of and the code's own figure is the subtotal of what those lines come to. Rows switched off in the detailed budget are struck through and count for nothing, the same as they do there.</p>
    <p class='mt-3'>The detailed budget carries a Value Engineering Item column for attaching cost codes to items in bulk, and for filtering and grouping the budget by them. To print any of it, use Export on the item's page for one item priced under every alternate, or Export on the VE / Alternates list for the whole list — either as a report block or a CSV.</p>
    <p class='mt-3'>Set items and alternates up on a template and their formulas carry into every estimate you start from it.</p>`
  },
  {
    id: 200,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'September 9th, 2026',
    title: 'Saved Views',
    helpArticleSlug: 'how-to-use-saved-views-in-the-detailed-budget',
    description: `<p>There is usually more than one way you want to look at the detailed budget: one set of columns and grouping for reviewing scope you have bought out, a different one for checking unit costs, another for the conversation you are about to have with the owner. Setting that up by hand every time you come back to it is the part nobody enjoys. You can now save an arrangement and load it again.</p>
    <p class='mt-3'>Arrange the budget how you want it, then save it under a name from Saved Views in the toolbar. The search, filters, columns and grouping all come back with it. Keep it to the one estimate, offer it on every estimate in the company, or save it onto a template so every estimate you start from that template already has it. Change something after loading a view and the picker reads Unsaved, so you can either save the change into the view or discard it, and Default puts the table back to how it opens.</p>`
  },
  {
    id: 199,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'September 3rd, 2026',
    title: 'Scenario Chart',
    helpArticleSlug: 'how-to-use-the-scenario-chart',
    featureIds: [FEATURE_IDS.ESTIMATE_SCENARIO_CHART_BUTTON],
    description: `<p>Finding the right mix of a building's programming is a guessing game today: change the room count, recalculate, change the footprint, recalculate, and try to hold the last five answers in your head. The Scenario Chart costs hundreds of mixes at once and draws them on one chart, so the trade-off between them is something you can look at rather than remember.</p>
    <p class='mt-3'>Open it from Scenario in the Inputs panel of an estimate. Choose which programming inputs the chart sweeps and how far each one moves — an input with no range of its own starts at 20% either side of where it sits today — and every combination in that range is costed and drawn as a line across the axes. Pick what you are measuring against with the control beside the axes: total cost, or cost per any design field, so you can chase the cheapest building or the cheapest cost per bed. Lines run from blue to red by that measure, so the extremes are findable by eye.</p>
    <p class='mt-3'>Drag down an axis to keep only the lines passing through a range of it. Brushes compose, so pinning the inputs you are sure about narrows what is still possible for the rest. Hover a line to see the value it holds on every axis and what it costs.</p>
    <p class='mt-3'>To put a chart in a report, add a Scenario Chart block and choose its axes and scenarios in the block's settings. The block stores the inputs and not the cost, so a printed chart shows what those mixes cost today rather than what they cost the day it was set up.</p>`
  },
  {
    id: 198,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'September 1st, 2026',
    title: 'Comp Quick View',
    description: `<p>Hovering over a comp in the detailed budget now shows a quick view of the comp's cost and design data.</p>`
  },
  {
    id: 197,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'August 27th, 2026',
    title: 'Detailed Budget Improvements',
    description: `<p>You can now group rows in the detailed budget by multiple types of items at once.</p>`
  },
  {
    id: 196,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'August 27th, 2026',
    title: 'Dynamic Cost Tags',
    description: `<p>Tagging cost fields by hand is fine until the estimate moves. You tag everything over $1M, the design changes, a few fields cross the line the other way, and now your tag analytics are telling you something that was true last week. A dynamic tag group takes that job off you: you write the rules once, and every cost field is re-sorted every time the estimate recalculates.</p>
    <p class='mt-3'>Add one from Cost Tags on a template or an estimate, switch on Dynamic, and choose whether it applies to cost fields, cost sub fields, or both. Each tag gets its own rules — Total Cost, Unit Cost, Quantity, % of Total Direct Cost, or whether the field has an awarded bid package. Drag the tags into the order you want them tested, since a field takes the first tag it matches, and mark one as the default to catch everything left over.</p>
    <p class='mt-3'>Everything that already reads tags reads these ones too — tag analytics, grouping and columns in the detailed budget, your reports and exports. Indirect cost formulas can now reference a tag's total as well, so a fee or a contingency can be driven by whatever is in that bucket today rather than by a number you remember to update. Set the group up on a template and the rules carry into every estimate you start from it.</p>`
  },
  {
    id: 195,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'August 24th, 2026',
    title: 'Group the Detailed Budget by UOM',
    description: `<p>You can now group the detailed budget by UOM — pick it from the Group menu and every cost field driven by the same design field sits together under one total. Each group also carries that design field's quantity and unit cost, so you can see what the estimate costs per SF of footprint or per ton of steel.</p>`
  },
  {
    id: 194,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'August 19th, 2026',
    title: 'Detailed Inputs',
    helpArticleSlug: 'how-to-use-detailed-inputs-to-drive-estimate-inputs-from-a-spreadsheet',
    description: `<p>Some programming inputs take a bit of math to get to, like a unit mix that adds up to a square footage or a takeoff you've been keeping outside Zebel. You can now do that math in the estimate itself. Open Detailed Inputs from the Inputs panel on an estimate or from a programming template, and lay it out however you want, or import something you've already built.</p>
    <p class='mt-3'>To use a number you've worked out, right-click the cell it's in and bind it to a programming input, then set that input's value source to Detailed Input. The input reads from that cell from then on, so when you change an assumption the estimate updates with it. Set this up on a programming template and both the math and the bindings carry into every estimate you start from it.</p>`
  },
  {
    id: 193,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'August 10th, 2026',
    title: 'Bid Package Improvements - Import Lump Sum by %',
    helpArticleSlug: 'how-to-import-a-lump-sum-on-a-bid',
    description: `<p>Import Lump Sum can now split by custom percentages. Pick it and you get a percent box on every cost field and detail row in the bid package, so you can enter the split yourself instead of using one of the automatic methods. Cost fields with detail rows under them add up their rows for you, and it all has to come to 100% before you can import.</p>`
  },
  {
    id: 192,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'July 31st, 2026',
    title: 'Comp Unit Cost Visualization',
    helpArticleSlug: 'how-to-use-the-comp-unit-cost-visualization',
    description: `<p>There's a new page in estimate analytics that charts your estimate against its comps in every cost category, so you can see where you land in the range they set. Switch between total cost and unit cost, and pick whichever design field you want to divide by.</p>`
  },
  {
    id: 191,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'July 27th, 2026',
    title: 'Your Profile & Profile Photos',
    helpArticleSlug: 'how-to-manage-your-profile-profile-photo',
    description: `<div>
    <p>Your account settings now have a home of their own. The old "My Account" popup is now a full profile page — click your avatar in the bottom-left and choose My Account to open it.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Profile Photo</strong> — Upload a photo of yourself and it shows up next to your name across the workspace — the nav, comments, estimate contributors, active editors, and your precon team.</li>
      <li><strong>Personal Details</strong> — Update your first and last name, and change your password, all from one page.</li>
    </ul>

    <p class='mt-3'>Drag an image onto the photo card or click Upload Photo (JPG or PNG) to get started.</p>
    </div>`
  },
  {
    id: 190,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'July 16th, 2026',
    title: 'Improved Bid Package Analytics',
    helpArticleSlug: 'how-to-use-bid-package-analytics',
    description: `<p>We've completetly overhauled the bid package analytics page to make it easier to understand and use. The analytics page now includes high level summary metrics, as well as detailed breakdowns of bids and bidders across bid packages in an estimate. All of the new analytics can also be added to your estimate reports.</p>`
  },
  {
    id: 189,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'July 10th, 2026',
    title: 'Detail Estimating Improvements',
    description: `<p>You can now bulk add many detail rows at once in the detailed budget and in bid packages.</p>`
  },
  {
    id: 188,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'July 10th, 2026',
    title: 'Cost Field Escalation Overrides',
    description: `<p>Some cost items escalate differently than the rest of your project — think volatile materials like steel, concrete, or copper. Build custom escalations in your settings and assign one to an individual cost field, so that field uses its own escalation to drive its cost from the comps instead of the estimate's base rate.</p>
    <p class='mt-3'>Set it from the new Escalation column in a template's or estimate's cost data. The field's calculated Unit Cost and the estimate's Total Cost update to reflect the override, and it flows through to your reports and exports. Leave a field unset and it keeps using the estimate/template escalation as before.</p>`
  },
  {
    id: 187,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'July 9th, 2026',
    title: 'Custom Unit Cost Displays',
    helpArticleSlug: 'how-to-add-a-custom-unit-cost-display',
    description: `<p>Inside of the detailed budget, budget views and the estimate report you can now add custom unit cost displays to any cost field. This will let you report any cost item by any unit of measure across the entire estimate.</p>`
  },
  {
    id: 186,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'July 7th, 2026',
    title: 'Comparison Improvements',
    description: `<p>You can now save a comparison's layout to your comparison templates, so you can reuse it in future comparisons.</p>`
  },
  {
    id: 185,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'July 6th, 2026',
    title: 'Bid Package Improvements',
    description: `<p>There's now a new column in the bid package page that shows the estimated cost of cost fields and detail rows.</p>`
  },
  {
    id: 184,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'July 2nd, 2026',
    title: 'Detailed Budget Improvements',
    description: `<p>There is a new column in the detailed budget that shows each rows % of total cost of the estimate.</p>`
  },
  {
    id: 183,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'July 2nd, 2026',
    title: 'Estimate Report Improvements',
    description: `<p>Input blocks now have a "Hide zero dollar rows" setting in their Structure section, so programming items with a zero design value are hidden from the report.</p>`
  },
  {
    id: 182,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'July 1st, 2026',
    title: 'Estimate Report Improvements',
    description: `<p>You can now toggle report blocks on and off without having to delete them. Hide a block to keep it out of the exported report, then flip it back on whenever you need it — no more rebuilding blocks you removed.</p>`
  },
  {
    id: 181,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'June 30th, 2026',
    title: 'Bid Package Improvements: Tag Bids and Bidders',
    helpArticleSlug: 'how-to-tag-bids-in-a-bid-package',
    description: `<p>You can now tag both bids and bidders right inside your bid packages, so you can organize and group them however your team thinks about your trades and partners. Pick tags as you add a bid or bidder, or manage them anytime from the bid package. You can even templatize them, so they're automatically created when you spin up your bid packages. Advanced analytics around these tags are coming soon.</p>`
  },
  {
    id: 180,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'June 25th, 2026',
    title: 'Bid Package Improvements: Plugs on Bid Responses',
    helpArticleSlug: 'how-to-add-a-plug-to-a-bid-form-response',
    description: `<p>You can now add plugs to bid form responses that are tied to a cost code inside of bid packages.`
  },
  {
    id: 179,
    category: NEWS_CATEGORIES.MARKET_DATA,
    date: 'June 23rd, 2026',
    title: 'Improved Market Data Filters',
    helpArticleSlug: 'how-to-use-the-market-data-filters',
    description: `<p>You can now filter market data by estimates and historical data.</p>`
  },
  {
    id: 178,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 23rd, 2026',
    title: 'Consolidated Estimate Improvements',
    helpArticleSlug: 'how-to-create-and-read-a-consolidated-estimate',
    description: `<p>Spinning up a consolidated estimate is a lot quicker now. Instead of adding child estimates one at a time, you can build all of them together on a new consolidated estimate form and create the whole thing in one go.</p>`
  },
  {
    id: 177,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 22nd, 2026',
    title: 'Tag Analytics Improvements',
    helpArticleSlug: 'how-to-read-the-tags-analytics',
    description: `<p>Breaking down tags by cost code now includes detail rows as well, so you can drill into the cost fields behind each tag.</p>`
  },
  {
    id: 176,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 22nd, 2026',
    title: 'Re-sync Comp Values 🔄',
    helpArticleSlug: 'how-to-re-sync-a-comps-values',
    description: `<div>
    <p>Comps capture a snapshot of a source project record at the moment you add them. When that record's cost or design data changes, you can now pull the latest values back into the comp without removing and re-adding it — and without losing your estimate-side adjustments.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Re-sync Values</strong> — A new "Re-sync Values" action refreshes a comp with the current cost and design data from its source record.</li>
      <li><strong>Cost &amp; Design Independently</strong> — Choose to re-sync cost data, design data, or both, so you only update what you need.</li>
      <li><strong>Your Edits Are Kept</strong> — Estimate-side adjustments like enable/disable toggles and premiums are preserved through the re-sync.</li>
      <li><strong>Two Places to Trigger It</strong> — Run a re-sync from the comp card on the estimate summary, or straight from the comp's column menu in the detailed budget.</li>
    </ul>

    <p class='mt-3'>Re-syncing overwrites the comp's snapshotted values and can't be undone, so you'll be asked to confirm before it runs.</p>
    </div>`
  },
  {
    id: 175,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 19th, 2026',
    title: 'A Smarter Cost Breakdown 📊',
    helpArticleSlug: 'how-to-use-the-cost-breakdown-analytics',
    description: `<div>
    <p>We've given the cost breakdown on your estimates a big upgrade. Instead of one fixed view, you can now slice your costs whatever way fits the conversation, chart it however tells the story best, and take it with you — export it, or drop it straight into a report.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Group it your way</strong> — Break costs down by cost category or code, category and field groups, your tag groups, or any budget view — and drill into any level of the WBS.</li>
      <li><strong>Pick a chart</strong> — See the same data as a bar, pie, treemap, or Pareto chart.</li>
      <li><strong>Export to CSV</strong> — Pull the breakdown into a spreadsheet in a click, in your chosen display currency.</li>
      <li><strong>Add it to a report</strong> — Drop a Cost Breakdown chart into any Estimate Report, with the grouping and chart type fully configurable.</li>
    </ul>

    <p class='mt-3'>Find it under the Cost Breakdown tab in your estimate's Analytics.</p>
    </div>`
  },
  {
    id: 174,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'June 18th, 2026',
    title: 'Manual Value Units',
    helpArticleSlug: 'how-to-use-a-manual-value-unit-of-measure',
    description: `<p>You can now select any of your custom value units for a manual value unit on a cost field, cost sub field, or estimate cost field.</p>`
  },
  {
    id: 173,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'June 18th, 2026',
    title: 'Archive Value Units',
    helpArticleSlug: 'how-to-archive-a-value-unit',
    description: `<p>Value units can now be archived, allowing you to hide them from the value unit picker.</p>`
  },
  {
    id: 172,
    category: NEWS_CATEGORIES.INTEGRATIONS,
    date: 'June 15th, 2026',
    title: 'BuildingConnected Integration is now in Beta 🚀',
    description: `<div>
    <p>Our BuildingConnected integration is now available in beta! Connect your Autodesk BuildingConnected account to Zebel and pull your bid packages and bids straight into your estimates — no more manual re-keying of bidder pricing.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Connect Your Account</strong> — Install the integration from Settings → Integrations and authorize Zebel to access your BuildingConnected data.</li>
      <li><strong>Link Bid Packages</strong> — Match a BuildingConnected bid package to a Zebel bid package and import its scope-specific items.</li>
      <li><strong>Map Line Items</strong> — Drag BuildingConnected line items onto your Zebel cost codes so submitted pricing lands exactly where it should.</li>
      <li><strong>Import Bids</strong> — Work through each bid on the package and import it in a click. Bids from a bidder already in your directory are matched automatically.</li>
    </ul>

    <p class='font-semibold mt-3 mb-1'>Project vs. Scope-Specific Bid Forms</p>
    <p>BuildingConnected splits its bid form questions into two levels, and Zebel mirrors that split so everything lands in the right place:</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Project Bid Form → Project Specific Items</strong> — The project-wide questions you ask of every bidder import into your estimate's <strong>Project Specific Items</strong>, which are automatically appended to every bid package. Import these first, from the Project Specific Items area in the bid packages section of your estimate (or set them up once on a record template).</li>
      <li><strong>Scope-Specific Bid Form → the bid package</strong> — The questions unique to a single scope import in Step 2 of the bid package import and attach only to that bid package, so each package keeps its own scope-specific items.</li>
    </ul>
    <p class='mt-3'>Because the project-wide items flow through to every package, import your Project Specific Items before bringing a bid package in through the integration — otherwise you'll be warned that you may miss project-wide questions.</p>

    <p class='mt-3'>Head to Settings → Integrations to connect your account, or reach out to support for a demo.</p>
    </div>`
  },
  {
    id: 171,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 10th, 2026',
    title: 'Budget View Creation Improvements',
    description: `<p>Adding cost codes to custom budget views is now much faster — the cost code picker lets you search and bulk-select multiple cost codes at once instead of adding them one at a time.</p>`
  },
  {
    id: 170,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 10th, 2026',
    title: 'Quick Comp Toggle in the Detailed Budget',
    helpArticleSlug: 'how-to-include-or-exclude-a-comp-with-the-quick-comp-toggle',
    description: `<p>You can now enable or disable all of a row's comps at once with a single toggle, rather than adjusting each one individually. This makes it much faster to test different scenarios and see how your costs respond as you bring comps in or out of the mix.</p>`
  },
  {
    id: 169,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 9th, 2026',
    title: 'Budget View Report Block — Depth Level',
    description: `<p>Budget View blocks in Estimate Reports now have a Depth Level setting, letting you roll the view up to only show items down to a certain level — perfect for sharing a high-level budget without every cost code underneath.</p>
    <p class='mt-3'>Open the new <strong>Structure</strong> panel in the block editor and drag the Depth Level slider. The slider adapts to each budget view — its maximum matches however many levels that view actually has — and folders at your chosen depth still show their rolled-up totals. Existing blocks keep showing all levels until you change them.</p>`
  },
  {
    id: 168,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 9th, 2026',
    title: 'Map Block in Estimate Reports',
    helpArticleSlug: 'how-to-add-a-map-block-to-an-estimate-report',
    description: `<div>
    <p>You can now add a Map block to your Estimate Reports, plotting your estimate alongside the comps it's based on directly inside any report.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Drag-In Block</strong> — A new "Map" card in the report sidebar drops a map into any report on estimates or templates.</li>
      <li><strong>Auto-Fit Map</strong> — The map automatically frames your estimate and comp locations, with a zoom adjustment to fine-tune how tight it sits.</li>
      <li><strong>Comp Toggles & Colors</strong> — Show or hide individual comps and set a custom marker color for each.</li>
      <li><strong>Location Cards</strong> — Optional cards listing each project's name, record details, address, and total cost beside the map.</li>
      <li><strong>Layout & Style</strong> — Choose the map position (full width, left, or right), map type, marker labels, height, and toggle the legend.</li>
    </ul>
    </div>`
  },
  {
    id: 167,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 9th, 2026',
    title: 'Comps Map on the Estimate Summary',
    helpArticleSlug: 'how-to-use-the-comps-map-on-the-estimate-summary',
    description: `<p>The Comps section on the estimate Summary page now includes an interactive map showing your estimate and the comps it's based on. Each comp card still links straight to its project record.</p>`
  },
  {
    id: 166,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 8th, 2026',
    title: 'Detail Budget Improvements',
    description: `<p>Hovering over a comp's cost field or category in the detailed budget now gives you a much clearer breakdown — the original total and unit cost, the adjustments applied, and the resulting adjusted cost.</p>`
  },
  {
    id: 165,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'June 4th, 2026',
    title: 'Bidder Tags',
    helpArticleSlug: 'how-to-use-tags-to-organize-your-contacts',
    description: `<p>We've added the ability to add tags to bidders in the directory. This will allow you to better organize your bidders and filter them when adding bids to estimates.</p>`
  },
  {
    id: 164,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'June 3rd, 2026',
    title: 'Import Bidders from CSV',
    helpArticleSlug: 'how-to-import-contacts-from-a-csv',
    description: `<p>You can now import bidders from a CSV file into the directory in the settings area.</p>`
  },
  {
    id: 163,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 1st, 2026',
    title: 'Cost Data Visualization',
    helpArticleSlug: 'how-to-use-the-cost-data-visualization',
    description: `<p>We've added a new Cost Data Visualization page to the estimate Analytics area. It shows your direct cost structure as a drill-down treemap — cells are sized by total cost, and clicking a cell zooms into its children so you can navigate from cost category groups down to individual cost codes.</p>`
  },
  {
    id: 162,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'June 1st, 2026',
    title: 'Company Branding Settings',
    helpArticleSlug: 'how-to-set-your-branding',
    description: `<div>
    <p>A new <strong>Branding</strong> tab under Settings → Company lets you control how your company appears on exported reports and shared documents.</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Logo</strong> — Upload your company logo (moved here from the Profile tab).</li>
      <li><strong>Color Palette</strong> — Save up to 20 brand colors. They show up as quick-pick swatches inside every color picker across the report editor.</li>
      <li><strong>Default Font</strong> — Pick a default font family used as the starting point for new reports.</li>
    </ul>
    </div>`
  },
  {
    id: 161,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 28th, 2026',
    title: 'Tito AI Agents are now in Beta 🚀',
    description: `<div>
    <p>Our Tito AI Agents that we released at Advancing Preconstruction in Phoenix are now in beta! You can now use AI agents to help you analyze your estimates to find issues and gaps. Some of the built-in agents include:</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Estimate Completeness</strong> — Flags missing or incomplete data and produces a readiness score</li>
      <li><strong>Unit Cost Reasonableness</strong> — Compares unit costs against comp benchmarks</li>
      <li><strong>Scope Gap Detector</strong> — Identifies potential scope gaps by comparing against comp benchmarks</li>
      <li><strong>Sensitivity Analysis</strong> — Identifies cost drivers with the highest impact on total cost</li>
    </ul>
    ... along with over 20 other agents that you can use to help you analyze your estimates! For more information or a demo, please reach out to support.
    </div>`
  },
  {
    id: 160,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'May 27th, 2026',
    title: 'Lump Sum Icon in Bids',
    helpArticleSlug: 'what-the-lump-sum-icon-on-a-bid-means',
    description: `<div>
    <p>We've added a new lump sum icon to bids, giving you a visual representation of whether a bid is a lump sum and how it was imported.</p>
    </div>`
  },
  {
    id: 159,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 27th, 2026',
    title: 'Cost Breakdown Chart Block in Estimate Reports',
    helpArticleSlug: 'how-to-add-a-cost-breakdown-chart-to-an-estimate-report',
    description: `<p>You can now add a Cost Breakdown block to your Estimate Reports. It displays your cost categories ranked by total cost with proportional bars — the same chart from the estimate Summary page, now available inside any report. Customize the bar color, font size, and padding in the block editor.</p>`
  },
  {
    id: 158,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 27th, 2026',
    title: 'Tables in Rich Text Report Block',
    helpArticleSlug: 'how-to-add-a-table-to-a-rich-text-block',
    description: `<div>
    <p>The rich text report block inside of estimate reports now supports tables. You can also use variables within the table cells.</p>
    </div>`
  },
  {
    id: 157,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 22nd, 2026',
    title: 'General Conditions Block in Estimate Reports',
    helpArticleSlug: 'how-to-add-a-gcs/grs-block-to-an-estimate-report',
    description: `<div>
    <p>You can now add a General Conditions block to your Estimate Reports, bringing your GC/GR schedule — milestones, cost items, and monthly cash flow — directly into any report.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Drag-In Block</strong> — A new "General Conditions" card in the report sidebar lets you drop the block into any report on estimates or templates.</li>
      <li><strong>Section Toggles</strong> — Show or hide the Milestones and Items sections independently.</li>
      <li><strong>Column Toggles</strong> — Show or hide the $/Mo, Burden, and Total columns in the Items table.</li>
      <li><strong>Cash Flow Row</strong> — Optionally include the Monthly Cash Flow summary row at the bottom of the Items table.</li>
    </ul>
    </div>`
  },
  {
    id: 156,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'May 22nd, 2026',
    title: 'Export to Report Replaces Export to PDF',
    helpArticleSlug: 'how-to-use-export-to-report',
    description: `<div>
    <p>The "Export to PDF" option across the app has been replaced with "Export to Report." Instead of generating a static PDF, exporting now creates a fully editable report in the Estimate Reports area.</p>

    <p class='font-semibold mt-3 mb-1'>What's Changed</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Detailed Budget</strong> — Export to Report creates a Cost Summary report.</li>
      <li><strong>Estimate Overview</strong> — Export to Report creates an Estimate Overview report.</li>
      <li><strong>Tag Group Analytics</strong> — Export to Report creates a Tag Group Analytics report.</li>
      <li><strong>Bid Package Detail</strong> — Export to Report creates a Bid Package Detail report.</li>
      <li><strong>Bid Package List</strong> — Export to Report creates a Bid Package Summary report.</li>
      <li><strong>Budget Views</strong> — Export to Report creates a Budget View report.</li>
      <li><strong>AI Agent Results</strong> — Export to Report creates an AI Agent Result report.</li>
      <li><strong>Estimate Inputs</strong> — Export to Report creates an Input Summary report.</li>
    </ul>

    <p class='mt-3'>Each report starts with a cover page and content block, and can be further customized in the report editor before exporting to PDF. Reports are saved in the Reports tab so you can come back to them later.</p>
    </div>`
  },
  {
    id: 155,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 21st, 2026',
    title: 'New Input Summary Block in Estimate Reports',
    helpArticleSlug: 'how-to-add-an-inputs-block-to-an-estimate-report',
    description: `<div>
    <p>You can now add an Input Summary block to your Estimate Reports, giving you a clean summary of your estimate inputs directly inside any report.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Drag-In Block</strong> — A new "Input Summary" option in the report sidebar lets you drop a input summary block into any report.</li>
      <li><strong>Column Toggles</strong> — Show or hide the Value, Unit, and Tooltip columns, and drag to reorder them.</li>
      <li><strong>Programming Sections</strong> — Groups are organized under their programming sections with indented rows for easy reading.</li>
    </ul>
    </div>`
  },
  {
    id: 154,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 20th, 2026',
    title: 'Cost Summary Report Block — Show/Hide Direct & Indirect Costs',
    helpArticleSlug: 'how-to-show-or-hide-direct-and-indirect-costs-in-a-cost-estimate-block',
    description: `<p>Cost Summary blocks in Estimate Reports now have toggles to show or hide the direct costs and indirect costs sections. Open the Structure panel in the block editor to find the new switches.</p>`
  },
  {
    id: 153,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'May 20th, 2026',
    title: 'Project Profile Images',
    helpArticleSlug: 'how-to-add-a-profile-image-to-a-project',
    description: `<div>
    <p>You can now upload a profile image to your project records and estimates, giving you a visual representation of your project. This image will also show in the comparison PDF export that each project is included in.</p>
    </div>`
  },
  {
    id: 152,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 15th, 2026',
    title: 'Export GCs/GRs to CSV and PDF',
    helpArticleSlug: 'how-to-export-your-gcs/grs',
    description: `<div>
    <p>You can now export the GCs/GRs schedule from any estimate to CSV or PDF, matching the export experience already available on bid packages and other estimate views.</p>

    <p class='mt-3'>Open the GCs/GRs tab on any estimate and use the new Export menu in the top right.</p>
    </div>`
  },
  {
    id: 151,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'May 15th, 2026',
    title: 'Dashboard Defaults',
    helpArticleSlug: 'how-to-set-your-dashboard-defaults',
    description: `<div>
    <p>A new Dashboard settings page lets you configure the default sector type, division, and UOM used when landing on the dashboard or market pages. More dashboard configuration options coming soon!</p>
    </div>`
  },
  {
    id: 150,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 15th, 2026',
    title: 'Duplicate Estimate Improvements',
    helpArticleSlug: 'how-to-duplicate-an-existing-estimate',
    description: `<p>When duplicating an estimate, you can now optionally copy all of your documents and drawings from the original estimate to the new version.</p>`
  },
  {
    id: 149,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'May 15th, 2026',
    title: 'Comparison Notes',
    description: `<p>You can now add notes to individual rows on the comparison page, making it easy to capture context, assumptions, or callouts directly alongside your comparison data.</p>`
  },
  {
    id: 148,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 14th, 2026',
    title: 'Reorder Columns in Report Blocks',
    helpArticleSlug: 'how-to-reorder-the-columns-in-a-report-block',
    description: `<div>
    <p>You can now drag and drop to reorder columns in estimate report block editors. The column order you set is reflected in both the live preview and PDF exports.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Drag-and-Drop Column Ordering</strong> — Grab the drag handle next to any column in the Columns tab and move it to your preferred position.</li>
      <li><strong>All Block Types Supported</strong> — Cost Summary, Budget View, Bid Package Summary, and Comparison blocks all support column reordering.</li>
      <li><strong>Backward Compatible</strong> — Existing report blocks continue to display columns in their original order until you customize them.</li>
    </ul>

    <p class='mt-3'>Open any report block editor, go to the Columns tab, and drag columns into the order you want.</p>`
  },
  {
    id: 147,
    category: NEWS_CATEGORIES.INTEGRATIONS,
    date: 'May 14th, 2026',
    title: 'Filter by Procore Integration',
    description: `<div>
    <p>We've added a new filter to the project database that allows you to filter by projects imported from Procore.</p>
    </div>`
  },
  {
    id: 146,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 12th, 2026',
    title: 'Visualize Input Formulas',
    helpArticleSlug: 'how-to-visualize-your-input-formulas',
    description: `<div>
    <p>See how your estimate and template inputs connect at a glance with a new interactive formula graph.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Formula Visualizer</strong> — A "Visualize Formulas" option on estimate inputs and template inputs opens a graph where every node is a design field used in your inputs, with arrows pointing from referenced fields to the formulas that use them.</li>
    </ul>

    <p class='mt-3'>Open the inputs menu on any estimate or the template inputs page and select "Visualize Formulas" to try it out. Reach out to support with questions.</p>
    </div>`
  },
  {
    id: 145,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'May 1st, 2026',
    title: 'Import Lump Sum on a Bid',
    helpArticleSlug: 'how-to-import-a-lump-sum-on-a-bid',
    description: `<div>
    <p>When a sub returns a single number instead of a line-item breakdown, you can now import that lump sum on the bid and have it automatically distributed across the bid's cost fields.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Import Lump Sum Action</strong> — A new "Import Lump Sum" option in the bid actions menu opens a modal where you can enter the total value and pick how it should be distributed.</li>
      <li><strong>Split Evenly</strong> — Divide the lump sum equally across every cost field on the bid.</li>
      <li><strong>Split by Estimate Value Weighted</strong> — Distribute the lump sum across cost fields proportional to each field's estimate value, so larger line items get a larger share.</li>
      <li><strong>Split by Another Bid's Value Weighted</strong> — Use another bidder's submitted values in the same package as the weights, so the imported lump sum mirrors the cost shape of a reference bid.</li>
      <li><strong>Detail Row Aware</strong> — When a cost field has detail rows, its share is automatically split evenly across its detail rows.</li>
    </ul>

    <p class='mt-3'>Open the actions menu on any bid in a bid package and select "Import Lump Sum" to give it a try. Reach out to support with questions.</p>
    </div>`
  },
  {
    id: 144,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'April 29th, 2026',
    title: 'Drawings',
    description: `<div>
    <p>Upload your project drawings into Zebel, mark them up with annotations, and tie those annotations directly to the cost fields they impact — keeping your scope, drawings, and budget all in one place.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Drawing Set Uploads</strong> — Upload multi-page PDF drawing sets to any estimate. Each page is automatically processed into its own plan sheet, ready to view and annotate.</li>
      <li><strong>High-Performance Viewer</strong> — Pan, zoom, and navigate large drawings smoothly with a tile-based viewer built for full-resolution construction documents.</li>
      <li><strong>Thumbnail Sidebar</strong> — Jump between pages of a drawing set with searchable thumbnails on the left side of the viewer.</li>
      <li><strong>Annotations</strong> — Draw rectangular annotations on any drawing to call out scope, RFIs, risks, or anything else that matters. Add a title, notes, and color so each annotation reads at a glance.</li>
      <li><strong>Cost Field Associations</strong> — Link annotations to specific cost fields in your estimate so the connection between a drawing markup and the budget line it affects is always clear.</li>
      <li><strong>Annotations in the Detailed Budget</strong> — A new Drawings column on the detailed budget shows an icon next to any cost field that has linked annotations. Click it to preview the annotation, jump to the drawing, or edit the annotation right from the budget.</li>
    </ul>

    <p class='mt-3'>Head to the Files tab in any estimate to upload your first drawing set under the Drawings area. If you'd like a demo or have questions, please reach out to support.</p>
    </div>`
  },
  {
    id: 143,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'April 27th, 2026',
    title: 'Bid Package Improvements — Project Specific Items',
    helpArticleSlug: 'how-to-set-up-project-specific-items-for-your-bid-packages',
    description: `<div>
    <p>Capture estimate-wide bid requirements in one place and have them automatically included with every bid package you send out.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Project Specific Items</strong> — Build a single bid form on the estimate that is appended to the end of every bid package, so bidders see consistent estimate-wide questions and requirements no matter which package they're responding to.</li>
      <li><strong>Template Support</strong> — Set up the project specific items once on a record template and it will be automatically created on every new estimate spun up from that template.</li>
    </ul>

    <p class='mt-3'>If you want a demo or you would like more information, reach out to support.</p>
    </div>`
  },
  {
    id: 142,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 24th, 2026',
    title: 'Improved Estimate Design Data Analytics',
    description: `<div>
    We've added a new Design Data Visualization page to the Estimate Analytics page that shows a comparison of estimate design values versus comp design values.
    </div>`
  },
  {
    id: 141,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 24th, 2026',
    title: 'Estimate Inputs Sidebar Update',
    description: `<div>
    The inputs sidebar inside of estimates now lives inside the Detailed Budget tab.
    </div>`
  },
  {
    id: 140,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'April 23rd, 2026',
    title: 'Bid Package Improvements',
    description: `<div>
    <p>More flexibility when structuring bid packages and comparing bidder pricing on alternates.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Custom Bid Package Sections</strong> — Add your own sections to any bid package within an estimate to organize scope however your project needs.</li>
      <li><strong>Bidder Alternates</strong> — Add alternates to any bid package so each bidder can swap in or layer on alternate pricing alongside their base bid.</li>
    </ul>
    </div>`
  },
  {
    id: 139,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 21st, 2026',
    title: 'Bid Package Summary Blocks in Estimate Reports',
    helpArticleSlug: 'how-to-add-a-bid-package-summary-block-to-an-estimate-report',
    description: `<div>
    <p>You can now add a Bid Package Summary block to your Estimate Reports, giving you a clean overview of all bid packages directly inside any report.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Drag-In Summary</strong> — A new "Bid Packages" section in the report sidebar lets you drop a summary block into any report.</li>
      <li><strong>Column Toggles</strong> — Show or hide the Bids, Status, Selected Bidder, Estimated Cost, Total Submitted, Total Adjustments, Awarded Cost, and Overruns columns.</li>
    </ul>
    </div>`
  },
  {
    id: 138,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 22nd, 2026',
    title: 'Bid Package Detail Blocks in Estimate Reports',
    helpArticleSlug: 'how-to-add-a-bid-package-detail-block-to-an-estimate-report',
    description: `<div>
    <p>The full bid package leveling view is now available as a block in your Estimate Reports — every bidder, every levelled bid, every alternate, side-by-side and ready to share.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Drag-In Detail</strong> — The "Bid Packages" section in the report sidebar now lists every bid package on the estimate. Drop one in to render its full leveling table — Summary, Price &amp; Scope, and any bid form sections — inline in the report.</li>
      <li><strong>Selected &amp; Preferred Highlighting</strong> — The selected bid is called out with a green check, and the preferred bid is shown in a softer dashed treatment so the bid status reads at a glance.</li>
      <li><strong>Bidder Visibility</strong> — Toggle individual bidders on and off per block. Hide a bidder you don't want to show clients without losing the data.</li>
      <li><strong>Section Visibility</strong> — Each bid form section (Alternates, Inclusions, scope-specific items, anything you've named) gets its own toggle, and hiding a section also hides any items that were promoted to a cost field.</li>
      <li><strong>Summary Row Toggles</strong> — Show or hide Total Base Bid, Total Adjustments/Plug, and Bidder Notes independently in the Summary section.</li>
      <li><strong>Spread Column</strong> — Optional Low/High Spread column when you have two or more bids to compare.</li>
      <li><strong>Layout &amp; Style</strong> — Padding, margins, page breaks, font size, header styling, and bottom borders all configurable per block.</li>
    </ul>
    </div>`
  },
  {
    id: 137,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 22nd, 2026',
    title: 'AI Agent Result Blocks in Estimate Reports',
    helpArticleSlug: 'how-to-add-an-ai-agent-result-block-to-an-estimate-report',
    description: `<div>
    <p>You can now drop completed AI Agent Results straight into your Estimate Reports. The full markdown analysis renders inline with the rest of your report, and you control exactly what shows up.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Drag-In Results</strong> — A new "AI Agent Results" section in the report sidebar lists every result for the current estimate, grouped by agent. Search to find the run you want.</li>
      <li><strong>Content Toggles</strong> — Show or hide the agent title and description, and the "Generated on" timestamp, independently per block.</li>
      <li><strong>Layout & Style</strong> — Full control over padding, margins, page breaks, font size, header colors, row striping, and bottom border so the analysis matches the rest of your report — including any tables embedded in the result.</li>
    </ul>
    </div>`
  },
  {
    id: 136,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 21st, 2026',
    title: 'Bid Package Summary Blocks in Estimate Reports',
    helpArticleSlug: 'how-to-add-a-bid-package-summary-block-to-an-estimate-report',
    description: `<div>
    <p>You can now add a Bid Package Summary block to your Estimate Reports, giving you a clean overview of all bid packages directly inside any report.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Drag-In Summary</strong> — A new "Bid Packages" section in the report sidebar lets you drop a summary block into any report.</li>
      <li><strong>Column Toggles</strong> — Show or hide the Bids, Status, Selected Bidder, Estimated Cost, Total Submitted, Total Adjustments, Awarded Cost, and Overruns columns.</li>
    </ul>
    </div>`
  },
  {
    id: 135,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 21st, 2026',
    title: 'Bid Package Summary Blocks in Estimate Reports',
    helpArticleSlug: 'how-to-add-a-bid-package-summary-block-to-an-estimate-report',
    description: `<div>
    <p>You can now add a Bid Package Summary block to your Estimate Reports, giving you a clean overview of all bid packages directly inside any report.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Drag-In Summary</strong> — A new "Bid Packages" section in the report sidebar lets you drop a summary block into any report.</li>
      <li><strong>Column Toggles</strong> — Show or hide the Bids, Status, Selected Bidder, Estimated Cost, Total Submitted, Total Adjustments, Awarded Cost, and Overruns columns.</li>
    </ul>
    </div>`
  },
  {
    id: 134,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 21st, 2026',
    title: 'Comparison Blocks in Estimate Reports',
    helpArticleSlug: 'how-to-add-a-comparison-block-to-an-estimate-report',
    description: `<div>
    <p>You can now add Comparison blocks to your Estimate Reports! Drag any of your existing comparisons into a report and configure exactly how it should appear.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Drag-In Comparisons</strong> — All of your saved comparisons appear in the report sidebar, ready to drop into any report.</li>
      <li><strong>Record Visibility</strong> — Toggle individual comparison records on or off per block.</li>
      <li><strong>Column Toggles</strong> — Show or hide Min, Average, Median, Max, Spread, and Variance columns. Variance is automatically enabled for variance comparisons.</li>
      <li><strong>Layout & Style</strong> — Full control over padding, margins, page breaks, fonts, header colors, row striping, indent size, and bottom border to match the rest of your report.</li>
    </ul>
    </div>`
  },
  {
    id: 133,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 16th, 2026',
    title: 'Programming Sections',
    helpArticleSlug: 'how-estimate-input-sections-work',
    description: `<div>
    <p>You can now organize your programming inputs into sections, giving you a higher level of grouping to keep your estimate inputs structured and easy to navigate. If you have any questions or need help getting started, please reach out to our support team.</p>
    </div>`
  },
  {
    id: 132,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 13th, 2026',
    title: 'Consolidated Estimate Improvements',
    description: `The detailed budget of a consolidated estimate now displays the total cost and unit cost for each child estimate.`
  },
  {
    id: 131,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'April 10th, 2026',
    title: 'Bid Package Improvements',
    description: `<div>
    <p>We've redesigned the Bid Package view with a cleaner layout and improved usability.</p>

    <p class='font-semibold mt-3 mb-1'>What's New</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Streamlined Layout</strong> — The bid package page now uses a simplified, scrollable layout that's easier to navigate.</li>
      <li><strong>Better Bid Comparison</strong> — Bids are now displayed side by side, making it easier to compare pricing across bidders at a glance.</li>
      <li><strong>Reorganized File Actions</strong> — Document actions are now grouped into a single button menu for a cleaner toolbar.</li>
      <li><strong>Faster Performance</strong> — The bid package page is now lighter and more responsive than before.</li>
    </ul>
    </div>`
  },
  {
    id: 130,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 9th, 2026',
    title: 'New Estimate Reports',
    helpArticleSlug: 'how-to-create-a-new-estimate-report',
    description: `<div>
    <p>We're excited to launch our completely redesigned Estimate Reports! The new report builder gives you full control over your report layout and content with a powerful block-based editor.</p>

    <p class='font-semibold mt-3 mb-1'>Build & Edit</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Visual Block Editor</strong> — Drag and drop content blocks onto the page. Click any block to edit it inline and see changes in real time.</li>
      <li><strong>Drag & Drop Reordering</strong> — Rearrange blocks by dragging them to a new position.</li>
      <li><strong>Multiple Reports</strong> — Create as many reports as you need per estimate or template, each tailored for a different audience.</li>
      <li><strong>Template Support</strong> — Configure reports on your templates and they'll carry over to every estimate created from that template.</li>
    </ul>

    <p class='font-semibold mt-3 mb-1'>Content Blocks</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Cover Page</strong> — Branded cover with your company logo, project name, and custom titles.</li>
      <li><strong>Section Headers</strong> — Customizable headings, text, and images across three zones.</li>
      <li><strong>Cost Estimate Table</strong> — Configurable depth, column visibility, tag groups, comps, and full style control.</li>
      <li><strong>Input Summary</strong> — Estimate overview groups and items with toggleable visibility.</li>
      <li><strong>Budget Views</strong> — Embed custom budget views with configurable columns and custom header names.</li>
      <li><strong>Tag Group Analytics</strong> — Cost breakdowns with summary stats, pie charts, and data tables.</li>
      <li><strong>Rich Text</strong> — Freeform formatted content anywhere in your report.</li>
      <li>More report blocks coming soon!</li>
    </ul>

    <p class='font-semibold mt-3 mb-1'>Customization & Export</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Dynamic Variables</strong> — Insert live data (project name, total cost, cost per design field, and more) that resolves automatically.</li>
      <li><strong>Style Controls</strong> — Customize fonts, colors, header backgrounds, row striping, indent sizes, margins, and page breaks per block.</li>
      <li><strong>PDF Export</strong> — Export to a polished PDF, or save directly to your project files.</li>
    </ul>

    <p class='mt-3'><strong>Legacy Reports:</strong> The previous report system is still accessible via the "View Legacy Report" button, but will be permanently removed on <strong>May 22nd, 2026</strong>. Please transition to the new reports before then.</p>
    <p>Head to the Report tab in any estimate or template to get started!</p>
    </div>`
  },
  {
    id: 129,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'April 9th, 2026',
    title: 'PDF Export Improvements',
    description: `<p>You can now set custom margins for PDF exports. More PDF improvements are coming soon!</p>`
  },
  {
    id: 128,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'March 24th, 2026',
    title: 'General Conditions & General Requirements (GCs/GRs) - Monthly Cash Flow',
    helpArticleSlug: 'how-to-read-the-monthly-cash-flow-on-gcs/grs',
    description: `<p>We've added a new monthly cash flow column to the GCs/GRs tab in estimates. This column displays the monthly cash flow for each cost field item.</p>`
  },
  {
    id: 127,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'March 23rd, 2026',
    title: 'Budget View Notes',
    helpArticleSlug: 'how-to-use-notes-on-a-budget-view',
    description: `<p>We've added a new notes column to Estimate Budget Views. You can now add notes to cost code groupings directly within the budget view, making it easier to provide context and track important details alongside your cost data.</p>`
  },
  {
    id: 126,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'March 6th, 2026',
    title: 'General Conditions & General Requirements (GCs/GRs)',
    helpArticleSlug: 'what-question-is-your-article-answeringhow-to-build-your-gcs/grs-schedule',
    description: `<p>We're excited to introduce a brand new GCs/GRs tab in your estimates! This feature gives you a dedicated space to plan and track general conditions and general requirements costs alongside your project schedule.</p>
    <ul class='list-disc list-inside my-2'>
      <li><strong>Milestone Schedule</strong> — Visualize your project milestones on a horizontal bar chart. Set start and finish months, assign colors, and see your entire schedule at a glance.</li>
      <li><strong>Cost Field Grid</strong> — A spreadsheet-like grid lets you allocate costs on a month-by-month basis. Set a $/month rate, burden percentage, and tie each item to a start and finish milestone with lead/lag offsets.</li>
      <li><strong>Automatic Cost Calculations</strong> — Total costs are automatically computed from your monthly allocations and flow directly into the Detailed Budget, keeping everything in sync.</li>
      <li><strong>Detail Row Support</strong> — Expand parent cost fields to see and edit individual detail rows, with parent totals automatically rolling up from their children.</li>
      <li><strong>Template Support</strong> — GCs/GRs can now be added to templates and will be copied over to new estimates created from that template.</li>
    </ul>
    <p>Head to the GCs/GRs tab in any estimate or record template to get started. If you would like a demo, please reach out to support!</p>`
  },
  {
    id: 125,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'February 20th, 2026',
    title: 'Bulk Actions in the Detailed Budget',
    helpArticleSlug: 'how-to-use-bulk-actions-in-the-detailed-budget',
    description: `<p>You can now select multiple cost fields or detail rows in the detailed budget and edit or delete them all at once. Use the checkboxes to select the rows you want to update, then choose from bulk actions like Set UOM, Set Note, Set Adjustments, or Delete. This makes it much quicker to manage your budget without having to update each row individually.</p>`
  },
  {
    id: 124,
    category: NEWS_CATEGORIES.INTEGRATIONS,
    date: 'February 18th, 2026',
    title: 'Procore Integration Rebuilt',
    helpArticleSlug: 'how-to-use-the-procore-integration',
    description: `<p>We've completely rebuilt the Procore integration from the ground up. The new integration makes it easier to map Procore cost codes to your Zebel cost structure, select budget views, and import cost data into your projects. Head to Settings &gt; Integrations to get started, or reach out to support for a demo.</p>`
  },
  {
    id: 123,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'January 22nd, 2026',
    title: 'Bid Package Analytics Improvements',
    description: `<p>The bid package analytics page now displays the total cost, total submitted cost, total adjusted cost and total overruns/savings for the selected bid packages in an estimate.</p>`
  },
  {
    id: 122,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'January 21st, 2026',
    title: 'Bid Package Improvements - Adjustments',
    description: `<p>We've updated how plugs are handled inside of bid packages. Plugs are now displayed as an 'adjustments' column beside the submitted bid and total cost columns.</p>`
  },
  {
    id: 121,
    category: NEWS_CATEGORIES.MARKET_DATA,
    date: 'January 20th, 2026',
    title: 'Filter by Scope Type',
    description: `<p>We've added a new filter to the market and project pages that allows you to filter by construction scope. This will allow you to see only projects that are "New Construction" or "Renovation".</p>`
  },
  {
    id: 120,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'January 14th, 2026',
    title: 'Project Record Scope Type',
    description: `<p>Records can now be marked as "New Construction" or "Renovation".</p>`
  },
  {
    id: 119,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'January 7th, 2026',
    title: 'Bid Package Improvements - Plugs Cost',
    description: `<p>Bid packages now display the total submitted cost and the total plugs cost, in addition to the total bid package cost.</p>`
  },
  {
    id: 118,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'December 30th, 2025',
    title: 'Bid Package Improvements - Bid Received Date',
    description: `<p>Each bid inside of bid packages now displays the received date. This makes it easier to track multiple bids from the same bidder and their respective received dates.</p>`
  },
  {
    id: 117,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'December 23rd, 2025',
    title: 'Tito AI Estimate Detailed Budget Insights',
    description: `<p>We've added a new feature to the estimate that allows you to generate AI insights for the detailed budget.</p>`
  },
  {
    id: 116,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'December 19th, 2025',
    title: 'Tito AI Comparison Insights',
    description: `<p>We've added a new feature to the comparison page that allows you to generate AI insights for a comparison. The insights are also included in the exported PDF files.</p>`
  },
  {
    id: 115,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'December 11th, 2025',
    title: '💰 Multi-Currency Support',
    description: `<div>
   Zebel now supports 20 different currencies across the platform. This update makes it easier to work with international data and compare projects globally. You can now:
    <ul class='list-disc list-inside my-2'>
      <li>Store historical data in multiple currencies across different projects</li>
      <li>Compare project costs using different currencies</li>
      <li>Estimate comps that use other currencies</li>
      <li>View market data in your preferred currency</li>
    </ul>
    <p>This lays the foundation for deeper financial insights and more flexible international workflows.</p>
    </div>`
  },
  {
    id: 114,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'December 10th, 2025',
    title: 'International Address Support',
    description: `<p>Zebel can now support projects in any country. Support for multiple currencies is coming soon!</p>`
  },
  {
    id: 113,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'December 8th, 2025',
    title: 'Inline Indirect Costs',
    description: `<p>One of our most requested features is now available! In addition to 'below the line' indirect costs, you can now add inline indirect costs to your estimates. This will allow you to add a percentage indirect costs to each cost item in your budget. For more info or to request a demo, please reach out to support.</p>`
  },
  {
    id: 112,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'November 26th, 2025',
    title: 'Enhanced Security - MFA',
    description: `<p>MFA (Multi-Factor Authentication) is now available for all users. To enable MFA for your organization, please reach out to support.</p>`
  },
  {
    id: 111,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'November 21st, 2025',
    title: 'Improved User Security',
    description: `<p>Inviting new users to your company now requires the user to have a valid company email address. This will block any user invites to personal or unwanted email addresses such as gmail, yahoo, etc.</p>`
  },
  {
    id: 110,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'November 13th, 2025',
    title: 'Budget View Adjustments',
    description: `<p>We've added the ability to make adjustments to your estimate Budget Views. You can now tweak a budget view to your liking or move costs around to fit a specific client's needs. The total at the bottom lets you know if you have levelled everything or are still over/under the original estimate total.</p>`
  },
  {
    id: 109,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'November 13th, 2025',
    title: 'Delete Detailed Budget Rows',
    description: `<p>We've added the ability to delete cost buckets, cost codes and detail rows from the detailed budget.</p>`
  },
  {
    id: 108,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'November 11th, 2025',
    title: 'Manual Value Default UOM',
    description: `<p>Cost codes and detail rows can now be set to a manual UOM value in the template settings.</p>`
  },
  {
    id: 107,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 30th, 2025',
    title: 'Detailed Budget Tag UOMs',
    description: `<p>When grouping the detailed budget by tags, you can now edit the UOM for each tag.</p>`
  },
  {
    id: 106,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 29th, 2025',
    title: 'Detailed Budget Grouping Improvements',
    description: `<p>When grouping the detailed budget by tags or bid packages, the total indirect costs will now be displayed at the bottom of the grouped rows.</p>`
  },
  {
    id: 105,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 28th, 2025',
    title: 'Estimate Indirect Cost Notes',
    description: `<p>We've added the ability to add notes to indirect costs in estimates. Notes can also be added to template indrect costs and they will be copied to new estimates created from that template.</p>`
  },
  {
    id: 104,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 22nd, 2025',
    title: 'Estimate Inputs Import from CSV',
    helpArticleSlug: 'how-to-export-and-import-estimate-inputs-as-a-csv',
    description: `<p>We've added the ability to import estimate inputs values from a CSV file. This allows you to quickly and easily import input items into your estimates. Reach out to support if you have questions.</p>`
  },
  {
    id: 103,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 6th, 2025',
    title: 'Estimate Budget Composition',
    description: `<p>We've added a new page to the estimate analytics that displays the budget composition of the estimate. This page includes a table with the variation percent of each cost item from the comps, and allows for a custom tolerance to be set.</p>`
  },
  {
    id: 102,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 6th, 2025',
    title: 'Selected Bidder Column',
    description: `<p>We've added a new column to the detailed budget that displays the selected bidder for each cost item.</p>`
  },
  {
    id: 101,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 3rd, 2025',
    title: 'Estimate Overview Export',
    description: `<p>Estimate Overview can now be exported to CSV and PDF files.</p>`
  },
  {
    id: 100,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 3rd, 2025',
    title: 'Estimate Inputs Export',
    helpArticleSlug: 'how-to-export-and-import-estimate-inputs-as-a-csv',
    description: `<p>Estimate Inputs can now be exported to CSV and PDF files.</p>`
  },
  {
    id: 99,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'September 30th, 2025',
    title: 'Indirect Cost Improvements',
    description: `<p>You can now add indirect costs from within the detailed budget page of estimates.</p>`
  },
  {
    id: 98,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'September 29th, 2025',
    title: 'Detailed Budget PDF Export',
    description: `<p>Detailed Budgets can now be exported to a PDF file inside of estimates.</p>`
  },
  {
    id: 97,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 26th, 2025',
    title: 'Improved Bid Package Analytics',
    description: `<p>We've updated the bid package analytics page inside of estimates. The improved page includes new charts for savings vs. overruns, bid statuses and more.</p>`
  },
  {
    id: 96,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 23rd, 2025',
    title: 'Bid Package PDF Export',
    description: `<p>Bid packages can now be exported to a PDF file inside of estimates.</p>`
  },
  {
    id: 95,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'September 22nd, 2025',
    title: 'Detailed Budget CSV Export',
    description: `<p>The detailed budget can now be exported to a CSV file inside of estimates.</p>`
  },
  {
    id: 94,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'September 19th, 2025',
    title: 'New Dashboard',
    description: `We've added a new dashboard to the app that displays company analytics, recently created items and more information about your company.`
  },
  {
    id: 93,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 12th, 2025',
    title: 'Bid Package Improvements',
    description: `<p>Bid packages inside of estimates now display the pre-bid value after the bid has been selected.</p>`
  },
  {
    id: 92,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'September 12th, 2025',
    title: 'Estimate Improvements',
    description: `<p>The UOM selector inside of the detailed budget has been improved. You can now search for design fields by name and select the UOM from a dropdown menu, allowing for faster and easier selection of UOMs.</p>`
  },
  {
    id: 91,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'September 11th, 2025',
    title: 'Comparison Improvements',
    description: `<p>We've rebuilt the compare page with a new design, improved performance, and much more. The new improvements include: </p>
    <ul class='list-disc list-inside'>
      <li>Improved design and UX</li>
      <li>Column resizing, hiding, pinning and filtering</li>
      <li>Improved performance - comparisons can now show unlimited rows and records without any performance issues</li>
      <li>New columns for min, max, average and median values</li>
    </ul>`
  },
  {
    id: 90,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 8th, 2025',
    title: 'Bid Package CSV Export',
    description: `<p>We've added the ability to export bid packages to a CSV file inside of estimates.</p>`
  },
  {
    id: 89,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 8th, 2025',
    title: 'Bid Notes',
    description: `<p>You can now add notes to bids inside of the bid package table.</p>`
  },
  {
    id: 88,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 4th, 2025',
    title: 'Bid Cost Codes Notes Icon',
    description: `<p>An icon now shows on cost codes with notes for each bid inside of the bid package table.</p>`
  },
  {
    id: 87,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 3rd, 2025',
    title: 'Add Cost Fields to Bid Package',
    description: `<p>Cost fields can now be mapped to bid packages from within the bid package page in an estimate.</p>`
  },
  {
    id: 86,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 3rd, 2025',
    title: 'Bid Package Delta Dollar Column',
    description: `<p>We've added a new column to the bid package table that displays the delta dollar difference between the preferred bid and the total cost.</p>`
  },
  {
    id: 85,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'September 2nd, 2025',
    title: 'Improved Bid Package Page',
    description: `<p>We've completely rebuilt the bid package page inside of estimates. The new bid package table now includes the same features as the detailed budget table, including column resizing, pinning, filtering, and more. More bid package improvements are coming soon!</p>`
  },
  {
    id: 84,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'September 2nd, 2025',
    title: 'Reset Estimate Escalation Rates',
    description: `<p>You can now easily reset the escalation rates for an estimate to the rates defined in an escalation table. This can be done from the estimate settings page. Hit the 'Reset Escalation Rates' button to open the dialog and select the escalation table to use.</p>`
  },
  {
    id: 83,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'August 26th, 2025',
    title: 'Improved Compare Escalation',
    description: `<p>You can now select any of your escalation tables to use when working on a comparison. If you want to configure multiple escalation tables, you can do this in the escalation table settings page.</p>`
  },
  {
    id: 82,
    category: NEWS_CATEGORIES.INTEGRATIONS,
    date: 'August 21st, 2025',
    title: 'Unit Mix Plugin',
    helpArticleSlug: 'what-is-the-unit-mix-plugin',
    description: `<p>We've added a new unit mix plugin geared specifically for multifamily, senior and student housing projects. This plugin allows you to configure the unit mix for each type of unit in your estimate. For more information or for a demo, please reach out to support.</p>`
  },
  {
    id: 81,
    category: NEWS_CATEGORIES.INTEGRATIONS,
    date: 'August 15th, 2025',
    title: 'Togal.AI Integration',
    helpArticleSlug: 'how-to-install-the-togal.ai-integration',
    description: `<p>The new Togal.AI integration is now available for all customers. You can now import data from Togal.AI takeoffs directly into your Zebel estimates with just a few clicks. The integration can be installed from the integrations page. For more information or for a demo, please reach out to support.</p>`
  },
  {
    id: 80,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'August 14th, 2025',
    title: 'Export Estimate History to PDF',
    description: `<p>Estimate history can now be exported to a PDF file inside of the estimate analytics page.</p>`
  },
  {
    id: 79,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'August 12th, 2025',
    title: 'Export Tags to PDF',
    description: `<p>Tags can now be exported to a PDF file inside of the estimate analytics page.</p>`
  },
  {
    id: 78,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'August 11th, 2025',
    title: 'Export Tags to CSV',
    description: `<p>Tags can now be exported to a CSV file inside of the estimate analytics page.</p>`
  },
  {
    id: 77,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'August 7th, 2025',
    title: 'Custom Budget Views PDF Export',
    description: `<p>Custom budget views inside of the estimate can now be exported to a PDF.</p>`
  },
  {
    id: 76,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'August 7th, 2025',
    title: 'Comparison PDF Improvements',
    description: `<p>Exporting a comparison to a PDF now includes the variance.</p>`
  },
  {
    id: 75,
    category: NEWS_CATEGORIES.INTEGRATIONS,
    date: 'August 6th, 2025',
    title: 'Zebel API',
    description: `<p>Our Zebel API V1 is now available for all customers. You can now use the Zebel API to build your own custom integrations with Zebel, including custom reporting, dashboards and data exports. The public API can be configured inside of the integrations settings page. More integrations are coming soon, including a Togal integration!</p>`
  },
  {
    id: 74,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'August 1st, 2025',
    title: 'New Compare Layout Item Selection',
    description: `<p>We've completely rebuilt the compare layout item selection process. You can now select multiple items at once and add them to the compare layout.</p>`
  },
  {
    id: 73,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'August 1st, 2025',
    title: 'Percent of Direct Cost Column',
    description: `<p>We've added a new column to the detailed budget that displays the percent of total direct cost for each cost item. This will allow you to see the percentage of the total direct cost that each cost item represents. The percent of direct cost column can be filtered to show cost items that are greater than or less than a specific percentage.</p>`
  },
  {
    id: 72,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'July 31st, 2025',
    title: 'Custom Budget Views CSV Export',
    description: `<p>Custom budget views can now be exported to a CSV file inside of estimates.</p>`
  },
  {
    id: 71,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'July 23rd, 2025',
    title: 'Estimate History CSV Export',
    description: `<p>We've added the ability to export the estimate history to a CSV file. This will allow you to download the history of changes made to an estimate.</p>`
  },
  {
    id: 70,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'July 23rd, 2025',
    title: 'Compare CSV Export Improvements',
    description: `<p>The comparison variance is now included in the compare CSV export. The variance will be included in the compare PDF export soon!</p>`
  },
  {
    id: 69,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'July 22nd, 2025',
    title: 'Escalated Project Record Total Cost',
    description: `<p>The total cost on the project record page now displays the escalated total cost, along with the escalation percentage.</p>`
  },
  {
    id: 68,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'July 18th, 2025',
    title: 'Custom Budget Views 🔥',
    helpArticleSlug: 'how-to-create-a-budget-view',
    description: `<p>We've added the ability to create custom budget views for estimates. This will allow you to view your cost data in any layout, in addition to the default layout setup in your template. For help setting up custom budget views, please reach out to support.</p>
    <p>The ability to export the new custom budget views to PDF will be coming soon!</p>
    `
  },
  {
    id: 67,
    category: NEWS_CATEGORIES.MARKET_DATA,
    date: 'July 10th, 2025',
    title: 'Market Data Filter Improvements',
    description: `<p>Market data can now be filtered to show only company data, only market data, or both company and market data.</p>`
  },
  {
    id: 66,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'July 9th, 2025',
    title: 'Improved Group by Cost Code in Project Record Cost Data',
    description: `<p>We've improved the group by cost code functionality in the project record cost data page. You can now group by cost code and the total cost will be displayed for each group, along with the ability to expand the group to see the individual cost fields.</p>`
  },
  {
    id: 65,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'July 8th, 2025',
    title: 'Improved Project Record Cost & Design Field Pages',
    description: `<p>We've rebuilt the record cost and design field pages to be more user friendly and efficient. The new pages include a new design, improved performance, searching, column resizing, and much more.</p>`
  },
  {
    id: 64,
    category: NEWS_CATEGORIES.MARKET_DATA,
    date: 'July 8th, 2025',
    title: 'Escalated Market Data',
    description: `<p>Market data can now be viewed with escalation applied.</p>`
  },
  {
    id: 63,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'July 7th, 2025',
    title: 'Enhanced Estimate Design Fields',
    description: `<p>We’ve added the ability to add custom design fields to your estimates. This is especially helpful when you need to include a unique or project-specific design field to an estimate. You can add these design fields either from the design field settings within the estimate or while configuring new inputs.</p>`
  },
  {
    id: 62,
    category: NEWS_CATEGORIES.MARKET_DATA,
    date: 'July 3rd, 2025',
    title: 'Enhanced Market Data Insights',
    description: `<p>We've added two new charts to the market data page:</p>
    <ul>
      <li>Unit Cost Distribution: Visualize the spread of unit costs to better understand market trends.</li>
      <li>Average Unit Cost by Year: Track the evolution of unit costs over time, providing a clearer comparison between your company and the market.</li>
    </ul>
    <p>These updates are designed to give you deeper insights into the market and how your company’s costs stack up, empowering better decision-making. More market updates are coming soon!</p>`
  },
  {
    id: 61,
    category: NEWS_CATEGORIES.MARKET_DATA,
    date: 'June 27th, 2025',
    title: 'Filter Market Data by Design Fields',
    description: `<p>There is a new filter on the market page that allows you to filter the market data by specific design field values.</p>`
  },
  {
    id: 60,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'June 26th, 2025',
    title: 'Improved Template Cost & Design Field Pages',
    description: `<p>We've rebuilt the template cost and design field pages to be more user friendly and efficient. The new pages include a new design, improved performance, searching, column resizing, and much more.</p>`
  },
  {
    id: 59,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 16th, 2025',
    title: 'Estimate Cost History',
    description: `<p>We've added a new chart to the estimate summary page that displays the cost changes to the estimate over time. When you hover over the chart, the specific reason for the cost change is displayed, along with the date and user who made the change.</p>`
  },
  {
    id: 58,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 10th, 2025',
    title: 'Create Estimate Cost Fields',
    description: `<p>Cost fields can now be added to a bucket inside of the detailed budget. This is useful for when you need to create unique cost codes for a project that are not already in the template.</p>`
  },
  {
    id: 57,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'June 10th, 2025',
    title: 'Canadian Project Support',
    description: `<p>We've added support for Canadian projects! You can now create projects with Canadian addresses, including support for Canadian provinces and postal codes. This makes it easier to manage projects across North America.</p>`
  },
  {
    id: 56,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 9th, 2025',
    title: 'Detailed Budget Grouping',
    description: `<p>We've added the ability to group cost codes in the detailed budget by tag group and bid package. Over the next few weeks we will continue to add more options and flexibility for grouping and viewing the detailed budget in different ways.</p>`
  },
  {
    id: 55,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 6th, 2025',
    title: 'Improved Estimate History',
    description: `<p>There is now a 'view history' button inside of the detailed budget for each cost bucket, cost field and detail row. This will allow you to see the history of changes made to each specific cost item over time.</p>`
  },
  {
    id: 54,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 6th, 2025',
    title: 'Detailed Budget Filtering',
    description: `<p>We've added filtering by cost code to the detailed budget! You can now filter the budget by total cost, UOM, quantity, adjustments, unit cost, bid package, tags and more.</p>`
  },
  {
    id: 53,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'June 2nd, 2025',
    title: 'Formula Improvements',
    description: `<p>We've made some improvements to the way formulas are handled across the application. These updates include:</p>
    <ul class='list-disc list-inside'>
      <li>Improved dark mode colors</li>
      <li>Searching for formula items now allows searching by cost code (in addition to label)</li>
      <li>Cost codes are now displayed inside of the formula beside the label</li>
      <li>Various performance and usability improvements</li>
    </ul>`
  },
  {
    id: 52,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 30th, 2025',
    title: 'New Detailed Budget',
    description: `<div><p>We've completely rebuilt the estimate detailed budget based on feedback we've received from customers. This update includes:
      <ul class='list-disc list-inside'>
        <li>New design</li>
        <li>Enhanced performance</li>
        <li>Improved usability</li>
        <li>Column resizing</li>
        <li>Column pinning</li>
        <li>Sticky headers (our most requested feature!)</li>
        <li>Search functionality</li>
        <li>Expand/collapse all rows</li>
        <li>Sticky total cost row</li>
      </ul>
      <br/>
      ...and much more! In the coming weeks, we will also add the ability to reorder columns and view bids inside of the detailed budget.
      </p></div>`
  },
  {
    id: 51,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 27th, 2025',
    title: 'Estimate Creation Improvements',
    description: `<p>You can now choose 'start from scratch' when selecting comps while creating an estimate. This will create an empty estimate with no comps.</p>`
  },
  {
    id: 50,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 9th, 2025',
    title: 'Estimate Dashboard Improvements',
    description: `<p>Comps on the estimate dashboard will now show the total direct cost for each comp. For consolidated estimates, the total cost of each child estimate are now also displayed on the dashboard.</p>`
  },
  {
    id: 49,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'May 8th, 2025',
    title: 'New Estimate Dashboard',
    description: `<p>We've redesigned the estimate dashboard to be more informative and helpful. The new dashboard includes more information about the estimate, including contributions by date and by user.</p>`
  },
  {
    id: 48,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 30th, 2025',
    title: 'Estimate Contributions',
    description: `<p>We've added a new page to estimate insights that displays contributions by date and by user. This will give insights into who has been working on an estimate and when.</p>`
  },
  {
    id: 47,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 25th, 2025',
    title: 'Improved Estimate Input Formatting',
    description: `<p>Estimate inputs now automatically display comma separators, making large numbers easier to read and interpret at a glance.</p>`
  },
  {
    id: 46,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'April 24th, 2025',
    title: 'Estimate Improvements',
    description: `<p>The sidebar inside of estimates is now resizable and collapsible. In addition, the inputs inside of the sidebar can now be all collapsed or expanded together. Improvements to the detailed budget are coming soon!</p>`
  },
  {
    id: 45,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'April 14th, 2025',
    title: 'Dark Mode',
    description: `<p>We've added a new dark mode to Zebel! Dark mode can be toggled on or off in the top header of the app.</p>`
  },
  {
    id: 44,
    category: NEWS_CATEGORIES.COMPARISON,
    date: 'March 28th, 2025',
    title: 'New Compare Page',
    description: `<p>We've completely rebuilt the Compare page! The new page features a new design, enhanced performance, new charting features, and much more. For a full demo of the new compare page, please reach out to support.</p>`
  },
  {
    id: 43,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'March 17th, 2025',
    title: 'Enhanced Escalation Features',
    description:
      '<p>Introducing the ability to leverage multiple escalation tables. You can now easily select the desired escalation table within a record template, and when creating estimates from that template, the selected escalation table will be used.</p>'
  },
  {
    id: 42,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'March 7th, 2025',
    title: 'Default Value Method for Design Fields',
    description:
      '<p>Design fields can now be configured to automatically populate their initial values from comps using statistical methods. When setting up a template, you can choose from Sum, Min, Max, Average, or Median methods which will set the initial design field value when creating an estimate from that template. If the design field is driven by a formula, the formula will still take precedence.</p><p>If you would like a demo or more information, please reach out to support.</p>'
  },
  {
    id: 41,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'March 7th, 2025',
    title: 'Estimate Comp Improvements',
    description: `<p>Settings for estimate comps have been moved out of the sidebar and into the estimate settings page. Comps can now also be edited directly from the detailed budget by clicking on a comp.</p>`
  },
  {
    id: 40,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'March 6th, 2025',
    title: 'Indirect Cost Improvements',
    description:
      '<p>The Indirect Cost Settings have been relocated from the estimate sidebar to the estimate settings page, providing more space for managing indirect costs. Additionally, indirect cost formulas can now be edited directly from the detailed budget by clicking on an indirect total cost.</p>'
  },
  {
    id: 39,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'March 6th, 2025',
    title: 'Indirect Cost Formula Improvements',
    description: '<p>Indirect cost formulas can now reference design fields inside of estimates.</p>'
  },
  {
    id: 38,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'March 5th, 2025',
    title: 'Estimate Design Data Improvements',
    description:
      '<p>A new page has been added to estimate settings, providing a list of design fields that are not included in the estimate inputs. This makes it easier to review and manage design data that falls outside of the typical inputs.</p>'
  },
  {
    id: 37,
    category: NEWS_CATEGORIES.MARKET_DATA,
    date: 'March 4th, 2025',
    title: 'Revamped Market Data Page',
    description: `<p>We've completely redesigned the Market Data page to offer a cleaner look, multi-sector support, and significantly improved performance. Additionally, estimates and records now include a Market tab, allowing for seamless comparisons with market data.</p>`
  },
  {
    id: 36,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'February 11th, 2025',
    title: 'Estimate Tag Enhancements',
    description: `<p>A new settings tab in Estimates lets you manage tags directly. While tags can still be set at the template level, they can now also be customized per estimate.</p>`
  },
  {
    id: 35,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'February 10th, 2025',
    title: 'Consolidated Estimate Improvements',
    description: `<p>There is a new tab on consolidated estimates that will display the design data for each child estimate. The design data can also be exported to a CSV file.</p>`
  },
  {
    id: 34,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'February 6th, 2025',
    title: 'Estimate Detailed Budget Improvements',
    description: `<p>In the detailed budget section of the estimate, you can now break down total direct and indirect costs by any unit of measure. The unit cost for each will also be displayed.</p>`
  },
  {
    id: 33,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'February 3rd, 2025',
    title: 'Custom Cost Filters',
    helpArticleSlug: 'how-to-create-a-custom-filter',
    description: `<p>Building on the custom design filters mentioned below, you can now create and manage custom cost filters in the settings area. These new filters let you refine projects and records based on total costs across the platform, expanding your ability to organize and analyze data your way.</p>`
  },
  {
    id: 32,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'February 3rd, 2025',
    title: 'Edit Indirect Cost Labels',
    description: `<p>You can now edit indirect cost labels from within the estimate, making it easier to make small adjustments if necessary.</p>`
  },
  {
    id: 31,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'February 3rd, 2025',
    title: 'Custom Value Units',
    description: `<p>Custom value units now allow up to 5 decmimal places.</p>`
  },
  {
    id: 30,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'January 31st, 2025',
    title: 'Estimates List & Filter Improvements',
    description: `<p>We've enhanced the estimate list page with new custom design filters (details below). Additionally, a new project database filter lets you sort records by historical data or estimates.</p>`
  },
  {
    id: 29,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'January 31st, 2025',
    title: 'Custom Design Filters',
    helpArticleSlug: 'how-to-create-a-custom-filter',
    description:
      '<p>You can now create and manage custom filters for design data in the settings area, making it easier to filter projects and records across the platform. Plus, you can reorder filters for better organization. Stay tuned - custom cost data filters are coming soon!</p>'
  },
  {
    id: 28,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'January 31st, 2025',
    title: 'Project Database Now Groups Consolidated Estimates',
    description:
      "<p>We've improved the project database to better organize estimates! Child estimates are now automatically grouped under their respective consolidated estimates, making it easier to manage and review related data. Additionally, child estimates can be reordered directly within the project database for greater flexibility."
  },
  {
    id: 27,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'January 23rd, 2025',
    title: 'The New Project Database is Here!',
    description: `
      <div class='space-y-2'>
  <p>We’ve rebuilt the project database with some exciting improvements:</p>
  <ul class='pl-3 mb-2'>
    <li><span class='font-medium'>New Design:</span> A clean, updated look for easier navigation.</li>
    <li><span class='font-medium'>Faster Performance:</span> Enjoy quicker load times and smoother interactions.</li>
    <li><span class='font-medium'>Improved Filters:</span> Sort and filter projects and records more effectively.</li>
    <li><strong class='font-medium'>Archiving:</strong> You can now archive projects and records to stay organized.</li>
    <li><strong class='font-medium'>New Dashboard:</strong> The record page now features a dashboard that summarizes record data for quick insights.</li>
  </ul>
  <p class="coming-soon">Coming soon: Custom filters to let you filter projects and records by any of your design fields!</p>
      </div>
    `
  },
  {
    id: 26,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'January 23rd, 2025',
    title: 'Revamped Cost Data Page',
    description: `<p>We've completely rebuilt the Cost Data page inside of the Project Database. The new page features a new design, enhanced performance, bulk actions, and much more.</p>`
  },
  {
    id: 25,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'January 16th, 2025',
    title: 'Bid Management is Now Available 🎉',
    description: `<p>We’re excited to announce that our Bid Management feature is now available to all existing customers! To explore how it works, you can request a demo or have the feature activated on your account by contacting our support team.</p>`
  },
  {
    id: 24,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'January 16th, 2025',
    title: 'Revamped Design Data Page',
    description: `<p>We’ve completely rebuilt the Design Data page in the Project Database! The new page features a new design, enhanced performance, bulk action capabilities, and much more. Stay tuned — an updated Cost Data page is coming soon!</p>`
  },
  {
    id: 23,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'January 1st, 2025',
    title: 'V1 Estimate - Depreciation',
    description: `<p>Legacy V1 estimates can no longer be created. Existing V1 estimates will be available to view until mid-2025 (exact date to be announced). If you are still using V1 estimates, please reach out to support for assistance upgrading to V2 estimates.</p>`
  },
  {
    id: 22,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'December 17th, 2024',
    title: 'Record Template - Detail Row Notes',
    description: `<p>Notes can now be added to detail rows in the record template settings. Any notes added in the template will be copied over to the estimate when the template is used.</p>`
  },
  {
    id: 21,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'December 3rd, 2024',
    title: 'Record Template - Duplicate',
    description: `<p>Record templates can now be duplicated. Duplicating a template will copy over all cost & design data, estimate configuration, formulas, tags, and more.</p>`
  },
  {
    id: 20,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'November 29th, 2024',
    title: 'V1 Estimate - Depreciation',
    description: `<p>We are phasing out V1 estimates. Starting January 1, 2025, it will no longer be possible to create new V1 estimates. However, you will still be able to view existing V1 estimates until mid-2025 (exact date to be announced).</p>`
  },
  {
    id: 19,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'November 28th, 2024',
    title: 'V2 Estimate - Detail Row Unit Cost Formulas',
    description: `<p>Unit cost formulas are now supported for detail rows in V2 estimates.</p>`
  },
  {
    id: 18,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'November 26th, 2024',
    title: 'V2 Estimate - Detail Row Negative Values',
    description: `<p>Detail rows now support unit cost values that are less than zero.</p>`
  },
  {
    id: 17,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'November 22nd, 2024',
    title: 'V2 Estimate - Default Detail Estimating',
    description: `<p>
You can now enable detail estimating by default for specific cost codes directly within the template settings. This can be configured on a per-cost-code basis or applied to multiple cost codes at once using the bulk update option. When detailed estimating is turned on by default, any estimate you create will automatically start in detail estimating mode.</p>`
  },
  {
    id: 16,
    category: NEWS_CATEGORIES.BID_PACKAGE,
    date: 'November 19th, 2024',
    title: 'V2 Estimate - Bid Management',
    description:
      'We’re excited to introduce our new bid management feature, now available in beta for V2 estimates! This powerful tool lets you efficiently manage bids tied to cost codes and detail rows across your estimate. You can organize bids within bid packages, compare them, and easily set the preferred or selected bid for each package. If you’re interested in joining the beta or would like a demo, please contact our support team.'
  },
  {
    id: 15,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'November 8th, 2024',
    title: 'V2 Estimate - Improved Detailed Budget Layout Preferences',
    description: `<p>We've added the ability to show or hide notes, files and tag icons for cost codes and detail rows in the layout preferences for V2 estimates. More advanced detailed budget layout preferences will be coming soon.</p>`
  },
  {
    id: 14,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'November 7th, 2024',
    title: 'New Directory Page',
    description: `<p>We've added a new Directory page in the Settings area! This page will help you manage contacts more effectively and lays the groundwork for our upcoming Bid Management feature.</p>`
  },
  {
    id: 13,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'November 6th, 2024',
    title: 'V2 Estimate - View Cost Codes By Tag',
    description: `<p>We've introduced a new feature in V2 estimates that lets you view all cost codes linked to a tag directly on the estimate analytics page. This update simplifies finding and managing cost codes associated with specific tags.</p>`
  },
  {
    id: 12,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'November 5th, 2024',
    title: 'V2 Estimate - Tagging Improvements',
    description:
      "<p>We've added the ability to quick edit a tag in v2 estimates by clicking on a tag and selecting a new tag. This should speed up the process of updating tags for cost codes.</p>"
  },
  {
    id: 11,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'November 5th, 2024',
    title: 'V2 Estimate - Tag Detail Rows',
    description: `<p>Tagging is now available for detail rows in V2 estimates. This allows for more flexibility in how cost data is tracked and reported. If you'd like a demo or more information, please reach out to support.</p>`
  },
  {
    id: 10,
    category: NEWS_CATEGORIES.HISTORICAL_RECORD,
    date: 'November 5th, 2024',
    title: 'Record Templates',
    description: `
  <p>We've fully redesigned the Cost and Design Data section in record templates to make managing and updating cost and design information easier than ever. This update introduces powerful new features, including:</p>
  
  <ul class='list-decimal list-inside pl-3 mb-2'>
    <li>Enhanced control over detail rows:
      <ul class='list-disc list-inside pl-3'>
        <li>Add, tag, bulk edit, and delete rows</li>
        <li>Set market links, units of measure (UOMs), and tags in bulk</li>
      </ul>
    </li>
    
    <li>Streamlined field management:
      <ul class='list-disc list-inside pl-3'>
        <li>Bulk edit, move between categories, or delete fields</li>
        <li>Bulk assign market links, UOMs, and tags</li>
      </ul>
    </li>
    
    <li>Improved user experience:
      <ul class='list-disc list-inside pl-3'>
        <li>Quickly add multiple categories, fields, and detail rows at once</li>
      </ul>
    </li>
  </ul>
  
  <p>For more details on these updates, please reach out to our support team.</p>

`
  },
  {
    id: 9,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 31st, 2024',
    title: 'V2 Estimate - Tagging',
    description: `<p>Tagging is now available in V2 estimates. You can tag cost codes to enable more detailed analytics, helping you gain insights by tracking key metrics. Tagging for sub-cost fields is coming soon. If you'd like assistance or a demo, please reach out to support.</p>`
  },
  {
    id: 8,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 29th, 2024',
    title: 'V2 Estimate - Performance Improvements',
    description: `<p>We've been working hard to improve the overall performance of v2 estimates. Selecting a template while creating a new estimate is now 60% faster than before.</p>`
  },
  {
    id: 7,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 23rd, 2024',
    title: 'V2 Estimate Unit Cost Formulas - Cost Categories',
    description: `<p>Unit cost formulas in v2 estimates now support referencing cost categories. This allows for more flexibility in how unit costs are calculated.</p>`
  },
  {
    id: 6,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'October 22nd, 2024',
    title: 'SSO OpenID Connect',
    description: `<p>Zebel now supports SSO OpenID Connect authentication. Please reach out to support for more information.</p>`
  },
  {
    id: 5,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'October 18th, 2024',
    title: 'File Upload Improvements',
    description: `<div>
      <p>File uploads across Zebel have been completelty rebuilt. The new file uploader is faster, more reliable, and supports a wider variety of file types. Some of the new features include:</p>
      <ul class='list-disc list-inside'>
        <li>PDFs and images can now be previewed in the browser before downloading</li>
        <li>The uploader supports drag and drop, and can upload multiple files at once</li>
        <li>The uploader will automatically retry failed uploads, and will alert you if any uploads fail</li>
        <li>Files can now be attached to cost codes in v2 estimates</li>
      </ul>
      </div>`
  },
  {
    id: 4,
    category: NEWS_CATEGORIES.MISCELLANEOUS,
    date: 'October 18th, 2024',
    title: 'Custom Value Units',
    description: `
      <p>Value units for design fields can now be customized in settings. This will allow for more flexibility for how many decimal places are displayed. Please reach out to support if you need help configuring these settings.</p>
      `
  },
  {
    id: 3,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 11th, 2024',
    title: 'V2 Estimate Consolidation',
    description: `
      <p>
      V2 estimates now support consolidation. This allows you to combine multiple estimates into a single consolidated estimate. Please reach out to support if you need help with this feature.
      </p>`
  },
  {
    id: 2,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 8th, 2024',
    title: 'V2 Estimate Indirect Cost Templates',
    description: `
      <p>
      Indirect costs for V2 estimates can now be setup inside of the record template settings. This will allow indirect costs to be more dynamic and flexible, as they now support custom formulas.
      </p>`
  },
  {
    id: 1,
    category: NEWS_CATEGORIES.ESTIMATE,
    date: 'October 1st, 2024',
    title: 'V2 Estimate Unit Cost Formulas',
    description: `
      <p>
     Custom formulas can now be used for unit cost overrides in V2 estimates. This will allow for more flexibility in how unit costs are calculated.
      </p>`
  }
];

// The updates a user is shown: every one, less those still behind a flag that does not admit them.
const getReleasedNews = (newsItems, user) => newsItems.filter((item) => !item.isReleased || item.isReleased(user));

const cookieName = 'zebel-news-last-seen-id';

const getLastSeenNewsId = () => {
  const lastSeenId = Number(Cookies.get(cookieName));
  return Number.isInteger(lastSeenId) ? lastSeenId : 0;
};

const getUnseenNewsCount = (user) => {
  const lastSeenId = getLastSeenNewsId();
  return getReleasedNews(news, user).filter((item) => item.id > lastSeenId).length;
};

// Stamped with the newest update this user can see, so one still behind a flag is not marked seen
// by somebody it was hidden from.
const updateLastSeenNews = (user) => {
  Cookies.set(cookieName, String(getReleasedNews(news, user)[0].id), { expires: 365 });
};

// The in-app path of the help article that covers an update, or null when none is named.
const getNewsGuidePath = (newsItem) => (newsItem.helpArticleSlug ? helpArticlePath(newsItem.helpArticleSlug) : null);

// --- Release tooltips ---------------------------------------------------------------------

// News dates are authored as display strings ('September 10th, 2026') rather than as ISO,
// and DATE_FORMATS.FULL is the format they are written in — so the announcement window is
// read off the same string the product updates page prints. A date that does not parse is
// treated as "no date" and never announces, rather than throwing on an authoring typo.
const parseAnnouncementDate = (date) => {
  const parsed = parse(date, DATE_FORMATS.FULL, new Date());
  return isValid(parsed) ? parsed : null;
};

const HTML_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", nbsp: ' ' };

// A release note runs to five paragraphs of HTML and a tooltip cannot carry that, so the
// tooltip shows the first paragraph as plain text and Read More hands the rest to the
// product updates page. Parsed with a regex rather than the DOM because this has to run in
// the `node` Vitest project, and because the input is our own authored markup rather than
// anything a user supplied.
const getAnnouncementSummary = (description) => {
  const firstParagraph = description.match(/<p[^>]*>([\s\S]*?)<\/p>/i)?.[1] ?? description;

  return firstParagraph
    .replace(/<[^>]+>/g, ' ')
    .replace(/&([a-z]+|#\d+);/gi, (entity, name) => HTML_ENTITIES[name.toLowerCase()] ?? entity)
    .replace(/\s+/g, ' ')
    .trim();
};

// An update opts in to announcing itself by naming feature ids. Everything else on the
// product updates page carries on being a page entry and nothing more, which is what keeps
// this from turning into a tooltip for every release.
const getAnnouncements = (newsItems, now) =>
  newsItems
    .filter((item) => item.featureIds?.length)
    .map((item) => ({ ...item, publishedAt: parseAnnouncementDate(item.date) }))
    .filter((item) => item.publishedAt && differenceInCalendarDays(now, item.publishedAt) <= FEATURE_ANNOUNCEMENT_MAX_AGE_DAYS);

// localStorage is shared with whatever a previous version of the app wrote there, so the
// stored value is treated as untrusted: anything that is not a list of integers reads as
// "nothing dismissed" rather than crashing the provider that every page mounts.
const normalizeDismissedIds = (stored) => (Array.isArray(stored) ? stored.filter(Number.isInteger) : []);

const getPendingAnnouncements = (announcements, dismissedIds) => {
  const dismissed = new Set(normalizeDismissedIds(dismissedIds));

  return announcements.filter((announcement) => !dismissed.has(announcement.id));
};

// Pre-fills what the sender would otherwise have to write for themselves. Support reads the
// subject to know which release is being asked about, and the body says what they want out
// of it — a walkthrough — because "more information" is not something anyone can action.
const getAnnouncementMailto = (title) => {
  const subject = encodeURIComponent(`Zebel: ${title}`);
  const body = encodeURIComponent(`Hi — I saw ${title} in Zebel and I'd like to know more about it. Could someone walk me through it?`);

  return `mailto:${FEATURE_ANNOUNCEMENT_SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
};

/**
 * Whether a Radix outside-interaction landed on a release tooltip — its card or its dim.
 *
 * For the `onInteractOutside` of a Sheet or Dialog that holds an announced control: without
 * it, answering the tooltip counts as a press outside the sheet and closes it.
 *
 * @param {CustomEvent} event Radix's interact-outside event, which carries the DOM event it wraps.
 */
const isFeatureAnnouncementEvent = (event) => {
  const target = event.detail?.originalEvent?.target ?? event.target;
  return Boolean(target?.closest?.(`[${FEATURE_ANNOUNCEMENT_LAYER_ATTRIBUTE}]`));
};

export {
  news,
  isFeatureAnnouncementEvent,
  getReleasedNews,
  getUnseenNewsCount,
  updateLastSeenNews,
  getNewsGuidePath,
  parseAnnouncementDate,
  getAnnouncementSummary,
  getAnnouncements,
  normalizeDismissedIds,
  getPendingAnnouncements,
  getAnnouncementMailto
};
